import type { KeywordCategoryKey } from './types'

export type PubMedKeywordFamily = {
    /**
     * Unique identifier for this configured keyword family.
     *
     * Existing IDs should remain stable because they may later be used by:
     * - filters
     * - exports
     * - endpoint-library mappings
     * - saved search settings
     */
    id: string

    /**
     * Human-readable label for this exact family.
     */
    label: string

    category: KeywordCategoryKey
    patterns: RegExp[]

    /**
     * Optional broader measure represented by this keyword family.
     *
     * Example:
     * measureId: 'acr_response'
     * measureLabel: 'ACR Response'
     */
    measureId?: string
    measureLabel?: string

    /**
     * Optional threshold, response level, cutoff, or variant underneath the
     * broader measure.
     *
     * Example:
     * thresholdId: 'acr20'
     * thresholdLabel: 'ACR20'
     */
    thresholdId?: string
    thresholdLabel?: string

    /**
     * Optional identifiers for future connection to the endpoint library.
     *
     * These are not currently used by the enrichment algorithm, but including
     * them here prevents another configuration redesign when the endpoint
     * library becomes the source of truth.
     */
    endpointSchemaId?: string
    endpointSubschemaId?: string
}

export const CORE_KEYWORD_FAMILIES: PubMedKeywordFamily[] = [
    // =========================
    // Epidemiology/ Burden of Disease
    // =========================
    {
        id: 'epidemiology',
        label: 'Epidemiology',
        category: 'epidemiology',
        patterns: [/\bepidemiolog(?:y|ic|ical)\b/i],
    },
    {
        id: 'prevalence',
        label: 'Prevalence',
        category: 'epidemiology',
        patterns: [/\bprevalence\b/i],
    },
    {
        id: 'incidence',
        label: 'Incidence',
        category: 'epidemiology',
        patterns: [/\bincidence\b/i, /\bincidence rate\b/i],
    },
    {
        id: 'disease_burden',
        label: 'Disease burden',
        category: 'epidemiology',
        patterns: [/\bburden of disease\b/i, /\bdisease burden\b/i],
    },
    {
        id: 'mortality',
        label: 'Mortality',
        category: 'epidemiology',
        patterns: [/\bmortality\b/i, /\bdeath rate\b/i],
    },
    {
        id: 'morbidity',
        label: 'Morbidity',
        category: 'epidemiology',
        patterns: [/\bmorbidity\b/i],
    },
    {
        id: 'risk_factors',
        label: 'Risk factors',
        category: 'epidemiology',
        patterns: [/\brisk factors?\b/i],
    },
    {
        id: 'comorbidities',
        label: 'Comorbidities',
        category: 'epidemiology',
        patterns: [/\bcomorbidit(?:y|ies)\b/i, /\bco-morbidit(?:y|ies)\b/i],
    },
    {
        id: 'natural_history',
        label: 'Natural history',
        category: 'epidemiology',
        patterns: [/\bnatural history(?: of disease)?\b/i],
    },
    {
        id: 'demographics',
        label: 'Demographic characteristics',
        category: 'epidemiology',
        patterns: [/\bdemographic characteristics?\b/i, /\bdemographics\b/i],
    },

    // =========================
    // Clinical efficacy and clinical effectiveness
    // =========================
    {
        id: 'efficacy',
        label: 'Efficacy',
        category: 'clinicalEfficacy',
        patterns: [/\befficacy\b/i],
    },
    {
        id: 'effectiveness',
        label: 'Effectiveness',
        category: 'clinicalEfficacy',
        patterns: [/\beffectiveness\b/i, /\breal[- ]world effectiveness\b/i],
    },
    {
        id: 'treatment_response',
        label: 'Treatment response',
        category: 'clinicalEfficacy',
        patterns: [/\btreatment response\b/i, /\bresponse rate\b/i],
    },
    {
        id: 'remission',
        label: 'Remission',
        category: 'clinicalEfficacy',
        patterns: [/\bremission\b/i],
    },
    {
        id: 'disease_progression',
        label: 'Disease progression',
        category: 'clinicalEfficacy',
        patterns: [/\bdisease progression\b/i, /\bprogression of disease\b/i],
    },
    {
        id: 'overall_survival',
        label: 'Overall survival',
        category: 'clinicalEfficacy',
        patterns: [/\boverall survival\b/i, /\bOS\b/],
    },
    {
        id: 'progression_free_survival',
        label: 'Progression-free survival',
        category: 'clinicalEfficacy',
        patterns: [/\bprogression[- ]free survival\b/i, /\bPFS\b/],
    },
    {
        id: 'physician_global_assessment',
        label: 'IGA/PhGA',
        category: 'clinicalEfficacy',
        patterns: [/\bPhGA\b/i, /\bphysician'?s? global assessment\b/i, /\bphysicians global assessment\b/i],
    },
    {
        id: 'cgi_s',
        label: 'CGI-S / Clinical Global Impression-Severity',
        category: 'clinicalEfficacy',
        patterns: [
            /\bCGI[- ]?S\b/i,
            /\bCGIS\b/i,
            /\bClinical Global Impression[- ]Severity\b/i,
            /\bClinical Global Impressions scale[- ]Severity\b/i,
            /\bClinician Global Impression of Severity\b/i,
        ],
    },
    {
        id: 'cgi_i',
        label: 'CGI-I / Clinical Global Impression-Improvement',
        category: 'clinicalEfficacy',
        patterns: [
            /\bCGI[- ]?I\b/i,
            /\bCGII\b/i,
            /\bClinical Global Impression[- ]Improvement\b/i,
            /\bClinician Global Impression of Improvement\b/i,
            /\bClinical Global Impressions scale[- ]Improvement\b/i,
        ],
    },
    // =========================
    // Economic / HEOR
    // =========================
    {
        id: 'healthcare_costs',
        label: 'Healthcare Costs',
        category: 'economic',
        measureLabel: 'Healthcare Costs',
        patterns: [/\bhealth[- ]?care costs?\b/i, /\bhealth[- ]?care expenditures?\b/i],
    },
    {
        id: 'medical_costs',
        label: 'Medical Costs',
        category: 'economic',
        measureLabel: 'Medical Costs',
        patterns: [
            /\bmedical costs?\b/i,
            /\bmedical expenditures?\b/i,
            /\bmedical service costs?\b/i,
            /\bnon[- ]?pharmacy medical costs?\b/i,
        ],
    },
    {
        id: 'pharmacy_costs',
        label: 'Pharmacy Costs',
        category: 'economic',
        measureLabel: 'Pharmacy Costs',
        patterns: [
            /\bpharmacy costs?\b/i,
            /\bpharmacy expenditures?\b/i,
            /\bprescription drug costs?\b/i,
            /\bmedication costs?\b/i,
            /\bRx costs?\b/i,
        ],
    },
    {
        id: 'all_cause_healthcare_costs',
        label: 'All-Cause Healthcare Costs',
        category: 'economic',
        measureLabel: 'All-Cause Healthcare Costs',
        patterns: [
            /\ball[- ]cause health[- ]?care costs?\b/i,
            /\btotal all[- ]cause health[- ]?care costs?\b/i,
            /\boverall all[- ]cause health[- ]?care expenditures?\b/i,
            /\bhealth[- ]?care costs? from all causes\b/i,
        ],
    },
    {
        id: 'all_cause_medical_costs',
        label: 'All-Cause Medical Costs',
        category: 'economic',
        measureLabel: 'All-Cause Medical Costs',
        patterns: [
            /\ball[- ]cause medical costs?\b/i,
            /\btotal all[- ]cause medical costs?\b/i,
            /\ball[- ]cause medical expenditures?\b/i,
            /\bmedical costs? from all causes\b/i,
        ],
    },
    {
        id: 'all_cause_inpatient_costs',
        label: 'All-Cause Inpatient / Hospitalization Costs',
        category: 'economic',
        measureLabel: 'All-Cause Medical Costs',
        thresholdLabel: 'All-Cause Inpatient / Hospitalization Costs',
        patterns: [
            /\ball[- ]cause inpatient costs?\b/i,
            /\ball[- ]cause hospitali[sz]ation costs?\b/i,
            /\bcosts? of all[- ]cause hospitali[sz]ations?\b/i,
            /\ball[- ]cause hospital admission costs?\b/i,
        ],
    },
    {
        id: 'all_cause_emergency_department_costs',
        label: 'All-Cause Emergency Department Costs',
        category: 'economic',
        measureLabel: 'All-Cause Medical Costs',
        thresholdLabel: 'All-Cause Emergency Department Costs',
        patterns: [
            /\ball[- ]cause emergency department costs?\b/i,
            /\ball[- ]cause emergency room costs?\b/i,
            /\ball[- ]cause (?:ED|ER) visit costs?\b/i,
            /\bcosts? of all[- ]cause emergency department visits?\b/i,
        ],
    },
    {
        id: 'all_cause_outpatient_costs',
        label: 'All-Cause Outpatient Costs',
        category: 'economic',
        measureLabel: 'All-Cause Medical Costs',
        thresholdLabel: 'All-Cause Outpatient Costs',
        patterns: [
            /\ball[- ]cause outpatient costs?\b/i,
            /\ball[- ]cause outpatient medical costs?\b/i,
            /\ball[- ]cause outpatient visit costs?\b/i,
            /\ball[- ]cause ambulatory care costs?\b/i,
        ],
    },
    {
        id: 'all_cause_pharmacy_costs',
        label: 'All-Cause Pharmacy Costs',
        category: 'economic',
        measureLabel: 'All-Cause Pharmacy Costs',
        patterns: [
            /\ball[- ]cause pharmacy costs?\b/i,
            /\ball[- ]cause pharmacy expenditures?\b/i,
            /\ball[- ]cause prescription drug costs?\b/i,
            /\ball[- ]cause medication costs?\b/i,
        ],
    },
    {
        id: 'cost_effectiveness',
        label: 'Cost-effectiveness',
        category: 'economic',
        patterns: [/\bcost[- ]effectiveness\b/i, /\bcost effectiveness analysis\b/i, /\bcost[- ]effective\b/i],
    },
    {
        id: 'cost_utility',
        label: 'Cost-utility',
        category: 'economic',
        patterns: [/\bcost[- ]utility\b/i, /\bcost utility analysis\b/i],
    },
    {
        id: 'icer',
        label: 'ICER',
        category: 'economic',
        patterns: [
            /\bICERs?\b/,
            /\bincremental cost[- ]effectiveness ratios?\b/i,
            /\bcost per QALY\b/i,
            /\bcost per quality[- ]adjusted life[- ]year\b/i,
            /\bcost per life[- ]year\b/i,
            /\bcost per LYG\b/i,
        ],
    },
    {
        id: 'budget_impact',
        label: 'Budget impact',
        category: 'economic',
        patterns: [
            /\bbudget impact\b/i,
            /\bincremental budget impact\b/i,
            /\bannual budget\b/i,
            /\bhealth plan budget\b/i,
            /\bplan costs?\b/i,
            /\bformulary impact\b/i,
        ],
    },
    {
        id: 'pmpm_pppm',
        label: 'PMPM / PPPM',
        category: 'economic',
        patterns: [/\bPMPM\b/, /\bper member per month\b/i, /\bPPPM\b/, /\bper patient per month\b/i],
    },
    {
        id: 'healthcare-resource-utilization',
        label: 'Healthcare Resource Utilization',
        category: 'economic',
        measureLabel: 'Healthcare Resource Utilization',
        patterns: [
            /\bHCRU\b/,
            /\bhealth[- ]?care resource utili[sz]ation\b/i,
            /\bhealth[- ]?care utili[sz]ation\b/i,
            /\bmedical resource utili[sz]ation\b/i,
            /\bhealth service utili[sz]ation\b/i,
        ],
    },
    {
        id: 'inpatient_utilization',
        label: 'Inpatient Stays / Hospitalizations',
        category: 'economic',
        measureLabel: 'Healthcare Resource Utilization',
        thresholdLabel: 'Inpatient Stays / Hospitalizations',
        patterns: [
            /\bhospitali[sz]ations?\b/i,
            /\bhospital admissions?\b/i,
            /\binpatient admissions?\b/i,
            /\binpatient stays?\b/i,
            /\bhospital stays?\b/i,
            /\binpatient days?\b/i,
        ],
    },
    {
        id: 'outpatient_utilization',
        label: 'Outpatient / Ambulatory Care Visits',
        category: 'economic',
        measureLabel: 'Healthcare Resource Utilization',
        thresholdLabel: 'Outpatient / Ambulatory Care Visits',
        patterns: [
            /\boutpatient visits?\b/i,
            /\boutpatient encounters?\b/i,
            /\bambulatory visits?\b/i,
            /\bambulatory care visits?\b/i,
            /\bambulatory care encounters?\b/i,
        ],
    },
    {
        id: 'emergency_department_utilization',
        label: 'Emergency Department Visits',
        category: 'economic',
        measureLabel: 'Healthcare Resource Utilization',
        thresholdLabel: 'Emergency Department Visits',
        patterns: [
            /\bemergency department visits?\b/i,
            /\bemergency room visits?\b/i,
            /\bED visits?\b/,
            /\bER visits?\b/,
            /\bemergency department encounters?\b/i,
            /\bemergency room encounters?\b/i,
        ],
    },
    {
        id: 'all_cause_healthcare_resource_utilization',
        label: 'All-Cause Healthcare Resource Utilization',
        category: 'economic',
        measureLabel: 'All-Cause Healthcare Resource Utilization',
        patterns: [
            /\ball[- ]cause health[- ]?care resource utili[sz]ation\b/i,
            /\ball[- ]cause health[- ]?care utili[sz]ation\b/i,
            /\ball[- ]cause medical resource utili[sz]ation\b/i,
            /\ball[- ]cause health service utili[sz]ation\b/i,
            /\ball[- ]cause HCRU\b/i,
            /\bhealth[- ]?care resource utili[sz]ation from all causes\b/i,
        ],
    },
    {
        id: 'all_cause_inpatient_utilization',
        label: 'All-Cause Inpatient Stays / Hospitalizations',
        category: 'economic',
        measureLabel: 'All-Cause Healthcare Resource Utilization',
        thresholdLabel: 'All-Cause Inpatient Stays / Hospitalizations',
        patterns: [
            /\ball[- ]cause inpatient stays?\b/i,
            /\ball[- ]cause inpatient admissions?\b/i,
            /\ball[- ]cause hospital admissions?\b/i,
            /\ball[- ]cause hospitali[sz]ations?\b/i,
            /\ball[- ]cause hospital stays?\b/i,
            /\bnumber of all[- ]cause hospitali[sz]ations?\b/i,
            /\brate of all[- ]cause hospitali[sz]ations?\b/i,
            /\bfrequency of all[- ]cause hospitali[sz]ations?\b/i,
            /\ball[- ]cause inpatient days?\b/i,
            /\ball[- ]cause length of stay\b/i,
        ],
    },
    {
        id: 'all_cause_emergency_department_utilization',
        label: 'All-Cause Emergency Department Visits',
        category: 'economic',
        measureLabel: 'All-Cause Healthcare Resource Utilization',
        thresholdLabel: 'All-Cause Emergency Department Visits',
        patterns: [
            /\ball[- ]cause emergency department visits?\b/i,
            /\ball[- ]cause emergency room visits?\b/i,
            /\ball[- ]cause (?:ED|ER) visits?\b/i,
            /\bnumber of all[- ]cause emergency department visits?\b/i,
            /\brate of all[- ]cause emergency department visits?\b/i,
            /\bfrequency of all[- ]cause (?:ED|ER) visits?\b/i,
        ],
    },
    {
        id: 'all_cause_outpatient_utilization',
        label: 'All-Cause Outpatient / Ambulatory Care Visits',
        category: 'economic',
        measureLabel: 'All-Cause Healthcare Resource Utilization',
        thresholdLabel: 'All-Cause Outpatient / Ambulatory Care Visits',
        patterns: [
            /\ball[- ]cause outpatient visits?\b/i,
            /\ball[- ]cause outpatient encounters?\b/i,
            /\ball[- ]cause ambulatory care visits?\b/i,
            /\ball[- ]cause ambulatory visits?\b/i,
            /\bnumber of all[- ]cause outpatient visits?\b/i,
            /\brate of all[- ]cause outpatient visits?\b/i,
            /\bfrequency of all[- ]cause ambulatory care visits?\b/i,
        ],
    },
    {
        id: 'all_cause_physician_office_utilization',
        label: 'All-Cause Physician / Office Visits',
        category: 'economic',
        measureLabel: 'All-Cause Healthcare Resource Utilization',
        thresholdLabel: 'All-Cause Physician / Office Visits',
        patterns: [
            /\ball[- ]cause physician visits?\b/i,
            /\ball[- ]cause office visits?\b/i,
            /\ball[- ]cause physician office visits?\b/i,
            /\ball[- ]cause primary care visits?\b/i,
            /\ball[- ]cause specialist visits?\b/i,
            /\bnumber of all[- ]cause physician visits?\b/i,
        ],
    },
    {
        id: 'all_cause_urgent_care_utilization',
        label: 'All-Cause Urgent Care Visits',
        category: 'economic',
        measureLabel: 'All-Cause Healthcare Resource Utilization',
        thresholdLabel: 'All-Cause Urgent Care Visits',
        patterns: [
            /\ball[- ]cause urgent care visits?\b/i,
            /\ball[- ]cause urgent care encounters?\b/i,
            /\bnumber of all[- ]cause urgent care visits?\b/i,
            /\brate of all[- ]cause urgent care visits?\b/i,
        ],
    },
    {
        id: 'productivity',
        label: 'Productivity',
        category: 'economic',
        patterns: [/\bproductivity\b/i, /\bwork productivity\b/i, /\bWPAI\b/i],
    },
    {
        id: 'absenteeism',
        label: 'Absenteeism/ Presenteeism',
        category: 'economic',
        patterns: [
            /\babsenteeism\b/i,
            /\bpresenteeism\b/i,
            /\bmissed work\b/i,
            /\bwork days missed\b/i,
            /\bschools days missed\b/i,
            /\bmissed school\b/i,
        ],
    },

    // =========================
    // Treatment patterns
    // =========================
    {
        id: 'treatment_patterns',
        label: 'Treatment patterns',
        category: 'treatmentPatterns',
        patterns: [/\btreatment patterns?\b/i],
    },
    {
        id: 'adherence',
        label: 'Adherence',
        category: 'treatmentPatterns',
        patterns: [/\badherence\b/i, /\bnon[- ]?adherence\b/i],
    },
    {
        id: 'compliance',
        label: 'Compliance',
        category: 'treatmentPatterns',
        patterns: [/\bcompliance\b/i, /\bnon[- ]?compliance\b/i],
    },
    {
        id: 'persistence',
        label: 'Persistence',
        category: 'treatmentPatterns',
        patterns: [/\bpersistence\b/i],
    },
    {
        id: 'mpr',
        label: 'Medication possession ratio',
        category: 'treatmentPatterns',
        patterns: [/\bmedication possession ratio\b/i, /\bMPR\b/],
    },
    {
        id: 'pdc',
        label: 'Proportion of days covered',
        category: 'treatmentPatterns',
        patterns: [/\bproportion of days covered\b/i, /\bPDC\b/],
    },
    {
        id: 'discontinuation',
        label: 'Discontinuation',
        category: 'treatmentPatterns',
        patterns: [/\bdiscontinuation\b/i, /\bdiscontinued\b/i],
    },
    {
        id: 'treatment_switching',
        label: 'Treatment switching',
        category: 'treatmentPatterns',
        patterns: [/\btreatment switch(?:ing)?\b/i, /\bswitching\b/i, /\bswitched\b/i],
    },
    {
        id: 'line_of_therapy',
        label: 'Line of therapy',
        category: 'treatmentPatterns',
        patterns: [
            /\blines? of therapy\b/i,
            /\bline of therapy\b/i,
            /\b1st line\b/i,
            /\bfirst line\b/i,
            /\b2nd line\b/i,
            /\bsecond line\b/i,
        ],
    },
    {
        id: 'dose_escalation',
        label: 'Dose escalation',
        category: 'treatmentPatterns',
        patterns: [/\bdose escalation\b/i, /\bdose increased?\b/i],
    },
    {
        id: 'rescue_medication',
        label: 'Rescue medication',
        category: 'treatmentPatterns',
        patterns: [/\brescue medication\b/i, /\brescue therapy\b/i],
    },
    {
        id: 'treatment_failure',
        label: 'Treatment failure',
        category: 'treatmentPatterns',
        patterns: [/\btreatment failure\b/i, /\btherapy failure\b/i],
    },
    {
        id: 'refractory',
        label: 'Refractory disease',
        category: 'treatmentPatterns',
        patterns: [/\brefractory\b/i],
    },
    {
        id: 'dosing_frequency',
        label: 'Dosing frequency',
        category: 'treatmentPatterns',
        patterns: [/\bdosing frequency\b/i, /\bdose frequency\b/i],
    },
    // =========================
    // Humanistic / PRO / QoL
    // =========================
    {
        id: 'quality_of_life',
        label: 'Quality of life',
        category: 'humanistic',
        patterns: [/\bquality[- ]of[- ]life\b/i, /\bquality of life\b/i, /\bQoL\b/i],
    },
    {
        id: 'hrqol',
        label: 'Health-related quality of life',
        category: 'humanistic',
        patterns: [/\bhealth[- ]related quality of life\b/i, /\bhealth related quality of life\b/i, /\bHRQoL\b/i],
    },
    {
        id: 'patient_reported_outcomes',
        label: 'Patient-reported outcomes',
        category: 'humanistic',
        patterns: [/\bpatient[- ]reported outcomes?\b/i, /\bpatient reported outcomes?\b/i, /\bPROs?\b/],
    },
    {
        id: 'eq_5d',
        label: 'EQ-5D',
        category: 'humanistic',
        patterns: [/\bEQ[- ]?5D\b/i, /\bEuroQol-5 Dimension questionnaire\b/i],
    },
    {
        id: 'eq_5d_3l',
        label: 'EQ-5D-3L',
        category: 'humanistic',
        patterns: [/\bEQ[- ]?5D[- ]?3L\b/i, /\bEQ5D3L\b/i, /\bEuroQol 5 Dimension 3 Level\b/i],
    },
    {
        id: 'eq_5d_5l',
        label: 'EQ-5D-5L',
        category: 'humanistic',
        patterns: [/\bEQ[- ]?5D[- ]?5L\b/i, /\bEQ5D5L\b/i, /\bEuroQol 5 Dimension 5 Level\b/i],
    },
    {
        id: 'sf_36',
        label: 'SF-36',
        category: 'humanistic',
        patterns: [/\bSF[- ]?36\b/i],
    },
    {
        id: 'sf_12',
        label: 'SF-12',
        category: 'humanistic',
        patterns: [/\bSF[- ]?12\b/i, /\bShort Form[- ]?12\b/i],
    },
    {
        id: 'utility',
        label: 'Utility',
        category: 'humanistic',
        patterns: [/\butility values?\b/i, /\bhealth utilit(?:y|ies)\b/i],
    },
    {
        id: 'daly',
        label: 'DALY',
        category: 'humanistic',
        patterns: [/\bDALYs?\b/i, /\bdisability[- ]adjusted life years?\b/i],
    },
    {
        id: 'caregiver_burden',
        label: 'Caregiver burden',
        category: 'humanistic',
        patterns: [/\bcaregiver burden\b/i],
    },
    {
        id: 'treatment_burden',
        label: 'Treatment burden',
        category: 'humanistic',
        patterns: [/\btreatment burden\b/i],
    },
    {
        id: 'treatment_satisfaction',
        label: 'Treatment satisfaction',
        category: 'humanistic',
        patterns: [/\btreatment satisfaction\b/i],
    },
    {
        id: 'medication_burden',
        label: 'Medication burden',
        category: 'humanistic',
        patterns: [/\bmedication burden\b/i, /\bpill burden\b/i],
    },
    {
        id: 'facit_fatigue',
        label: 'FACIT-Fatigue',
        category: 'humanistic',
        patterns: [
            /\bFACIT[- ]?F\b/i,
            /\bFACIT[- ]?Fatigue\b/i,
            /\bfunctional assessment of chronic illness therapy[- ]fatigue\b/i,
            /\bfunctional assessment of chronic illness therapy fatigue scale\b/i,
        ],
    },
    {
        id: 'promis',
        label: 'PROMIS',
        category: 'humanistic',
        patterns: [/\bPROMIS\b/],
    },
    {
        id: 'promis_depression',
        label: 'PROMIS Depression',
        category: 'humanistic',
        patterns: [/\bPROMIS depression\b/i, /\bPROMIS[- ]depression\b/i, /\bPROMIS depression scale\b/i],
    },
    {
        id: 'promis_anxiety',
        label: 'PROMIS Anxiety',
        category: 'humanistic',
        patterns: [/\bPROMIS anxiety\b/i, /\bPROMIS[- ]anxiety\b/i, /\bPROMIS anxiety scale\b/i],
    },
    {
        id: 'promis_fatigue',
        label: 'PROMIS Fatigue',
        category: 'humanistic',
        patterns: [
            /\bPROMIS[- ]?Fatigue\b/i,
            /\bPROMIS[- ]?Fatigue SF[- ]?10a\b/i,
            /\bPROMIS Fatigue Short Form 10a\b/i,
            /\bpatient[- ]reported outcomes measurement information system[- ]fatigue\b/i,
        ],
    },
    {
        id: 'hads',
        label: 'Hospital Anxiety and Depression Scale',
        category: 'humanistic',
        measureLabel: 'Hospital Anxiety and Depression Scale',
        patterns: [
            /\bHADS\b(?![- ]?(?:A|D|Anxiety|Depression)\b)/i,
            /\bHospital Anxiety and Depression Scale\b(?![-–— ]+(?:Anxiety|Depression)\b)/i,
        ],
    },
    {
        id: 'hads_a',
        label: 'HADS-A',
        category: 'humanistic',
        measureLabel: 'Hospital Anxiety and Depression Scale',
        thresholdLabel: 'HADS-A',
        patterns: [
            /\bHADS[- ]?A\b/i,
            /\bHADS[- ]?Anxiety\b/i,
            /\bHADS\s+anxiety\s+(?:score|subscale|domain)\b/i,
            /\bHospital Anxiety and Depression Scale[-–— ]+Anxiety\b/i,
            /\bAnxiety\s+subscale\s+of\s+(?:the\s+)?Hospital Anxiety and Depression Scale\b/i,
        ],
    },
    {
        id: 'hads_d',
        label: 'HADS-D',
        category: 'humanistic',
        measureLabel: 'Hospital Anxiety and Depression Scale',
        thresholdLabel: 'HADS-D',
        patterns: [
            /\bHADS[- ]?D\b/i,
            /\bHADS[- ]?Depression\b/i,
            /\bHADS\s+depression\s+(?:score|subscale|domain)\b/i,
            /\bHospital Anxiety and Depression Scale[-–— ]+Depression\b/i,
            /\bDepression\s+subscale\s+of\s+(?:the\s+)?Hospital Anxiety and Depression Scale\b/i,
        ],
    },
    {
        id: 'patient_global_assessment',
        label: 'Patient Global Assessment',
        category: 'humanistic',
        patterns: [/\bPaGA\b/i, /\bpatient global assessment\b/i, /\bpatient global assessment of severity\b/i],
    },
    {
        id: 'pgi_s',
        label: 'PGI-S / Patient Global Impression of Severity',
        category: 'humanistic',
        patterns: [/\bPGI[- ]?S\b/i, /\bPGIS\b/i, /\bpatient global impression(?:s)? of severity\b/i],
    },
    {
        id: 'pgi_c',
        label: 'PGI-C / Patient Global Impression of Change',
        category: 'humanistic',
        patterns: [/\bPGI[- ]?C\b/i, /\bPGIC\b/i, /\bpatient global impression(?:s)? of change\b/i],
    },
    /*
    {
        id: 'vas',
        label: 'Visual analog scale',
        category: 'humanistic',
        patterns: [/\bvisual analog(?:ue)? scale\b/i, /\bVAS\b/],
    },
    {
        id: 'nrs',
        label: 'Numeric rating scale',
        category: 'humanistic',
        patterns: [/\bnumeric rating scale\b/i, /\bNRS\b/],
    },*/

    // =========================
    // Safety
    // =========================
    /*
    {
        id: 'safety',
        label: 'Safety',
        category: 'safety',
        patterns: [/\bsafety\b/i, /\bsafety endpoint\b/i],
    },
    */
    {
        id: 'adverse_events',
        label: 'Adverse events',
        category: 'safety',
        patterns: [/\badverse events?\b/i, /\bAEs?\b/],
    },
    {
        id: 'serious_adverse_events',
        label: 'Serious adverse events',
        category: 'safety',
        patterns: [/\bserious adverse events?\b/i, /\bSAEs\b/],
    },
    {
        id: 'treatment_emergent_adverse_events',
        label: 'Treatment-emergent adverse events',
        category: 'safety',
        patterns: [/\btreatment[- ]emergent adverse events?\b/i, /\bTE[- ]?AEs?\b/i],
    },
    {
        id: 'tesae',
        label: 'Treatment-emergent serious adverse events',
        category: 'safety',
        patterns: [
            /\bTESAEs?\b/i,
            /\btreatment[- ]emergent serious adverse events?\b/i,
            /\btreatment[- ]emergent SAEs?\b/i,
        ],
    },
    {
        id: 'adverse_events_of_special_interest',
        label: 'Adverse events of special interest',
        category: 'safety',
        patterns: [/\badverse events? of special interest\b/i, /\bAESIs?\b/],
    },
    {
        id: 'aes_leading_to_discontinuation',
        label: 'AEs leading to treatment discontinuation',
        category: 'safety',
        patterns: [
            /\bAEs? leading to (?:study )?treatment discontinuation\b/i,
            /\badverse events? leading to (?:study )?treatment discontinuation\b/i,
            /\badverse events? leading to discontinuation\b/i,
        ],
    },
    {
        id: 'tolerability',
        label: 'Tolerability',
        category: 'safety',
        patterns: [/\btolerability\b/i],
    },
    {
        id: 'toxicity',
        label: 'Toxicity',
        category: 'safety',
        patterns: [/\btoxicity\b/i, /\btoxicities\b/i],
    },
    {
        id: 'side_effects',
        label: 'Side effects',
        category: 'safety',
        patterns: [/\bside effects?\b/i],
    },
    {
        id: 'discontinuation_due_to_adverse_events',
        label: 'Discontinuation due to adverse events',
        category: 'safety',
        patterns: [
            /\bdiscontinuation due to adverse events?\b/i,
            /\bdiscontinuation due to AEs\b/i,
            /\bdiscontinued due to adverse events?\b/i,
        ],
    },
    {
        id: 'immunogenicity_ada',
        label: 'Anti-drug antibodies / immunogenicity',
        category: 'safety',
        patterns: [
            /\banti[- ]drug antibodies\b/i,
            /\banti[- ]drug antibody\b/i,
            /\bADA incidence\b/i,
            /\bADA titer\b/i,
            /\bneutralizing antibodies\b/i,
            /\bNAbs?\b/i,
            /\bimmunogenicity\b/i,
        ],
    },
]

export const DISEASE_SPECIFIC_KEYWORD_FAMILIES: Record<string, PubMedKeywordFamily[]> = {
    rheumatoid_arthritis: [
        {
            id: 'acr_response',
            label: 'ACR Response',
            category: 'clinicalEfficacy',
            measureId: 'acr_response',
            measureLabel: 'ACR Response',
            endpointSchemaId: 'acr_response',
            patterns: [
                /\bACR\s*(?:20|50|70)\b/i,
                /\bAmerican College of Rheumatology response\b/i,
                /\bACR response\b/i,
            ],
        },
        {
            id: 'acr_20',
            label: 'ACR20',
            category: 'clinicalEfficacy',
            measureId: 'acr_response',
            measureLabel: 'ACR Response',
            thresholdId: 'acr20',
            thresholdLabel: 'ACR20',
            endpointSchemaId: 'acr_response',
            endpointSubschemaId: 'acr20',
            patterns: [
                /\bACR\s*20\b/i,
                /\b20% improvement in ACR\b/i,
                /\bAmerican College of Rheumatology 20% improvement\b/i,
            ],
        },
        {
            id: 'acr_50',
            label: 'ACR50',
            category: 'clinicalEfficacy',
            measureId: 'acr_response',
            measureLabel: 'ACR Response',
            thresholdId: 'acr50',
            thresholdLabel: 'ACR50',
            endpointSchemaId: 'acr_response',
            endpointSubschemaId: 'acr50',
            patterns: [
                /\bACR\s*50\b/i,
                /\b50% improvement in ACR\b/i,
                /\bAmerican College of Rheumatology 50% improvement\b/i,
                /\bACR\s*20[,\s/]+(?:and\s+)?50\b/i,
            ],
        },
        {
            id: 'acr_70',
            label: 'ACR70',
            category: 'clinicalEfficacy',
            measureId: 'acr_response',
            measureLabel: 'ACR Response',
            thresholdId: 'acr70',
            thresholdLabel: 'ACR70',
            endpointSchemaId: 'acr_response',
            endpointSubschemaId: 'acr70',
            patterns: [
                /\bACR\s*70\b/i,
                /\b70% improvement in ACR\b/i,
                /\bAmerican College of Rheumatology 70% improvement\b/i,
                /\bACR\s*(?:20|50)[,\s/]+(?:and\s+)?70\b/i,
            ],
        },
        {
            id: 'das28',
            label: 'DAS28',
            category: 'clinicalEfficacy',
            patterns: [/\bDAS28\b/i, /\bdisease activity score 28\b/i],
        },
        {
            id: 'das28_crp',
            label: 'DAS28-CRP',
            category: 'clinicalEfficacy',
            patterns: [
                /\bDAS28[- ]?CRP\b/i,
                /\bDAS28\s*\(CRP\)/i,
                /\bdisease activity score 28 using C[- ]reactive protein\b/i,
            ],
        },
        {
            id: 'das28_crp_remission',
            label: 'DAS28-CRP remission',
            category: 'clinicalEfficacy',
            patterns: [
                /\bDAS28 remission\s*<\s*2\.6\b/i,
                /\bDAS28[- ]?CRP remission\b/i,
                /\bdisease activity score assessing 28 joints.*remission.*<\s*2\.6\b/i,
            ],
        },
        {
            id: 'cdai',
            label: 'CDAI',
            category: 'clinicalEfficacy',
            patterns: [/\bCDAI\b/i, /\bclinical disease activity index\b/i],
        },
        {
            id: 'sdai',
            label: 'SDAI',
            category: 'clinicalEfficacy',
            patterns: [/\bSDAI\b/i, /\bsimplified disease activity index\b/i],
        },
        {
            id: 'low_disease_activity',
            label: 'Low disease activity',
            category: 'clinicalEfficacy',
            patterns: [
                /\blow disease activity\b/i,
                /\bLDA\b/i,
                /\bDAS28 LDA\b/i,
                /\bCDAI LDA\b/i,
                /\btime to LDA\b/i,
                /\bfirst occurrence of LDA\b/i,
            ],
        },
        {
            id: 'disease_activity_status',
            label: 'Disease activity',
            category: 'clinicalEfficacy',
            patterns: [/\bdisease activity\b/i, /\bdisease activity status\b/i],
        },
        {
            id: 'radiographic_progression',
            label: 'Radiographic progression',
            category: 'clinicalEfficacy',
            patterns: [
                /\bradiographic progression\b/i,
                /\bradiographic structural progression\b/i,
                /\bstructural progression\b/i,
                /\bjoint damage progression\b/i,
                /\bradiographic damage\b/i,
            ],
        },
        {
            id: 'modified_total_sharp_score',
            label: 'mTSS / modified total Sharp score',
            category: 'clinicalEfficacy',
            patterns: [
                /\bmTSS\b/i,
                /\bmodified total Sharp score\b/i,
                /\bmodified Sharp score\b/i,
                /\btotal Sharp score\b/i,
                /\bSharp(?:\/|-)van der Heijde score\b/i,
                /\bvan der Heijde modified Sharp score\b/i,
            ],
        },
        {
            id: 'swollen_joint_count',
            label: 'Swollen joint count',
            category: 'clinicalEfficacy',
            patterns: [
                /\bSJC\b/i,
                /\bswollen joint count\b/i,
                /\bswollen joints count\b/i,
                /\b66[- ]joint swelling count\b/i,
            ],
        },
        {
            id: 'tender_joint_count',
            label: 'Tender joint count',
            category: 'clinicalEfficacy',
            patterns: [
                /\bTJC\b/i,
                /\btender joint count\b/i,
                /\btender joints count\b/i,
                /\b68[- ]joint tenderness count\b/i,
            ],
        },
        {
            id: 'sf_36_ra',
            label: 'SF-36',
            category: 'humanistic',
            patterns: [/\bSF[- ]?36\b/i, /\bSF36\b/i, /\bSF[- ]?36v2\b/i, /\bshort form 36\b/i],
        },
        {
            id: 'haq_di',
            label: 'HAQ-DI',
            category: 'humanistic',
            measureLabel: 'HAQ-DI',
            patterns: [
                /\bHAQ[- ]?DI\b/i,
                /\bHAQ Disability Index\b/i,
                /\bHealth Assessment Questionnaire[- ]?Disability Index\b/i,
                /\bHealth Assessment Questionnaire Disability Index\b/i,
                /\bStanford Health Assessment Questionnaire Disability Index\b/i,
            ],
        },
        {
            id: 'rheumatoid_arthritis_related_healthcare_costs',
            label: 'RA-Related Healthcare Costs',
            category: 'economic',
            measureLabel: 'RA-Related Healthcare Costs',
            patterns: [
                /\brheumatoid arthritis[- ]related health[- ]?care costs?\b/i,
                /\brheumatoid arthritis[- ]related health[- ]?care expenditures?\b/i,
                /\bRA[- ]related health[- ]?care costs?\b/i,
                /\bRA[- ]related health[- ]?care expenditures?\b/i,
                /\bhealth[- ]?care costs? attributable to rheumatoid arthritis\b/i,
                /\bhealth[- ]?care expenditures? attributable to rheumatoid arthritis\b/i,
                /\bcosts? associated with rheumatoid arthritis\b/i,
            ],
        },
        {
            id: 'rheumatoid_arthritis_related_medical_costs',
            label: 'RA-Related Medical Costs',
            category: 'economic',
            measureLabel: 'RA-Related Medical Costs',
            patterns: [
                /\brheumatoid arthritis[- ]related medical costs?\b/i,
                /\brheumatoid arthritis[- ]related medical expenditures?\b/i,
                /\bRA[- ]related medical costs?\b/i,
                /\bRA[- ]related medical expenditures?\b/i,
                /\bmedical costs? attributable to rheumatoid arthritis\b/i,
                /\bmedical expenditures? attributable to rheumatoid arthritis\b/i,
            ],
        },
        {
            id: 'rheumatoid_arthritis_related_inpatient_costs',
            label: 'RA-Related Inpatient / Hospitalization Costs',
            category: 'economic',
            measureLabel: 'RA-Related Medical Costs',
            thresholdLabel: 'RA-Related Inpatient / Hospitalization Costs',
            patterns: [
                /\brheumatoid arthritis[- ]related inpatient costs?\b/i,
                /\brheumatoid arthritis[- ]related inpatient medical costs?\b/i,
                /\brheumatoid arthritis[- ]related hospitali[sz]ation costs?\b/i,
                /\brheumatoid arthritis[- ]related hospital admission costs?\b/i,
                /\bRA[- ]related inpatient costs?\b/i,
                /\bRA[- ]related hospitali[sz]ation costs?\b/i,
                /\bRA[- ]related hospital admission costs?\b/i,
                /\bcosts? of hospitali[sz]ations? attributable to rheumatoid arthritis\b/i,
                /\binpatient costs? attributable to rheumatoid arthritis\b/i,
            ],
        },
        {
            id: 'rheumatoid_arthritis_related_emergency_department_costs',
            label: 'RA-Related Emergency Department Costs',
            category: 'economic',
            measureLabel: 'RA-Related Medical Costs',
            thresholdLabel: 'RA-Related Emergency Department Costs',
            patterns: [
                /\brheumatoid arthritis[- ]related emergency department costs?\b/i,
                /\brheumatoid arthritis[- ]related emergency room costs?\b/i,
                /\brheumatoid arthritis[- ]related (?:ED|ER) visit costs?\b/i,
                /\bRA[- ]related emergency department costs?\b/i,
                /\bRA[- ]related emergency room costs?\b/i,
                /\bRA[- ]related (?:ED|ER) visit costs?\b/i,
                /\bemergency department costs? attributable to rheumatoid arthritis\b/i,
                /\bcosts? of emergency department visits? attributable to rheumatoid arthritis\b/i,
            ],
        },
        {
            id: 'rheumatoid_arthritis_related_outpatient_costs',
            label: 'RA-Related Outpatient / Ambulatory Care Costs',
            category: 'economic',
            measureLabel: 'RA-Related Medical Costs',
            thresholdLabel: 'RA-Related Outpatient / Ambulatory Care Costs',
            patterns: [
                /\brheumatoid arthritis[- ]related outpatient costs?\b/i,
                /\brheumatoid arthritis[- ]related outpatient medical costs?\b/i,
                /\brheumatoid arthritis[- ]related outpatient visit costs?\b/i,
                /\brheumatoid arthritis[- ]related ambulatory care costs?\b/i,
                /\bRA[- ]related outpatient costs?\b/i,
                /\bRA[- ]related outpatient visit costs?\b/i,
                /\bRA[- ]related ambulatory care costs?\b/i,
                /\boutpatient costs? attributable to rheumatoid arthritis\b/i,
                /\bcosts? of outpatient visits? attributable to rheumatoid arthritis\b/i,
            ],
        },
        {
            id: 'rheumatoid_arthritis_related_pharmacy_costs',
            label: 'RA-Related Pharmacy Costs',
            category: 'economic',
            measureLabel: 'RA-Related Pharmacy Costs',
            patterns: [
                /\brheumatoid arthritis[- ]related pharmacy costs?\b/i,
                /\brheumatoid arthritis[- ]related drug costs?\b/i,
                /\brheumatoid arthritis[- ]related medication costs?\b/i,
                /\brheumatoid arthritis[- ]related prescription costs?\b/i,
                /\brheumatoid arthritis[- ]related prescription drug costs?\b/i,
                /\bRA[- ]related pharmacy costs?\b/i,
                /\bRA[- ]related drug costs?\b/i,
                /\bRA[- ]related medication costs?\b/i,
                /\bRA[- ]related prescription drug costs?\b/i,
                /\bpharmacy costs? attributable to rheumatoid arthritis\b/i,
                /\bdrug costs? attributable to rheumatoid arthritis\b/i,
                /\bmedication costs? attributable to rheumatoid arthritis\b/i,
                /\bprescription drug costs? attributable to rheumatoid arthritis\b/i,
            ],
        },
        {
            id: 'rheumatoid_arthritis_related_healthcare_resource_utilization',
            label: 'RA-Related Healthcare Resource Utilization',
            category: 'economic',
            measureLabel: 'RA-Related Healthcare Resource Utilization',
            patterns: [
                /\brheumatoid arthritis[- ]related health[- ]?care resource utili[sz]ation\b/i,
                /\brheumatoid arthritis[- ]related health[- ]?care utili[sz]ation\b/i,
                /\brheumatoid arthritis[- ]related medical resource utili[sz]ation\b/i,
                /\brheumatoid arthritis[- ]related health service utili[sz]ation\b/i,
                /\brheumatoid arthritis[- ]related HCRU\b/i,
                /\bRA[- ]related health[- ]?care resource utili[sz]ation\b/i,
                /\bRA[- ]related health[- ]?care utili[sz]ation\b/i,
                /\bRA[- ]related HCRU\b/i,
                /\bhealth[- ]?care resource utili[sz]ation attributable to rheumatoid arthritis\b/i,
                /\bhealth[- ]?care utili[sz]ation attributable to rheumatoid arthritis\b/i,
            ],
        },
        {
            id: 'rheumatoid_arthritis_related_inpatient_utilization',
            label: 'RA-Related Inpatient Stays / Hospitalizations',
            category: 'economic',
            measureLabel: 'RA-Related Healthcare Resource Utilization',
            thresholdLabel: 'RA-Related Inpatient Stays / Hospitalizations',
            patterns: [
                /\brheumatoid arthritis[- ]related inpatient stays?\b/i,
                /\brheumatoid arthritis[- ]related inpatient admissions?\b/i,
                /\brheumatoid arthritis[- ]related hospital admissions?\b/i,
                /\brheumatoid arthritis[- ]related hospitali[sz]ations?\b/i,
                /\brheumatoid arthritis[- ]related hospital stays?\b/i,
                /\bRA[- ]related inpatient stays?\b/i,
                /\bRA[- ]related inpatient admissions?\b/i,
                /\bRA[- ]related hospital admissions?\b/i,
                /\bRA[- ]related hospitali[sz]ations?\b/i,
                /\bhospitali[sz]ations? attributable to rheumatoid arthritis\b/i,
                /\binpatient admissions? attributable to rheumatoid arthritis\b/i,
                /\bnumber of rheumatoid arthritis[- ]related hospitali[sz]ations?\b/i,
                /\brate of rheumatoid arthritis[- ]related hospitali[sz]ations?\b/i,
            ],
        },
        {
            id: 'rheumatoid_arthritis_related_emergency_department_utilization',
            label: 'RA-Related Emergency Department Visits',
            category: 'economic',
            measureLabel: 'RA-Related Healthcare Resource Utilization',
            thresholdLabel: 'RA-Related Emergency Department Visits',
            patterns: [
                /\brheumatoid arthritis[- ]related emergency department visits?\b/i,
                /\brheumatoid arthritis[- ]related emergency room visits?\b/i,
                /\brheumatoid arthritis[- ]related (?:ED|ER) visits?\b/i,
                /\bRA[- ]related emergency department visits?\b/i,
                /\bRA[- ]related emergency room visits?\b/i,
                /\bRA[- ]related (?:ED|ER) visits?\b/i,
                /\bemergency department visits? attributable to rheumatoid arthritis\b/i,
                /\bemergency room visits? attributable to rheumatoid arthritis\b/i,
                /\bnumber of rheumatoid arthritis[- ]related emergency department visits?\b/i,
                /\brate of rheumatoid arthritis[- ]related (?:ED|ER) visits?\b/i,
            ],
        },
        {
            id: 'rheumatoid_arthritis_related_outpatient_utilization',
            label: 'RA-Related Outpatient / Ambulatory Care Visits',
            category: 'economic',
            measureLabel: 'RA-Related Healthcare Resource Utilization',
            thresholdLabel: 'RA-Related Outpatient / Ambulatory Care Visits',
            patterns: [
                /\brheumatoid arthritis[- ]related outpatient visits?\b/i,
                /\brheumatoid arthritis[- ]related outpatient encounters?\b/i,
                /\brheumatoid arthritis[- ]related ambulatory care visits?\b/i,
                /\brheumatoid arthritis[- ]related ambulatory visits?\b/i,
                /\bRA[- ]related outpatient visits?\b/i,
                /\bRA[- ]related outpatient encounters?\b/i,
                /\bRA[- ]related ambulatory care visits?\b/i,
                /\boutpatient visits? attributable to rheumatoid arthritis\b/i,
                /\bambulatory care visits? attributable to rheumatoid arthritis\b/i,
                /\bnumber of rheumatoid arthritis[- ]related outpatient visits?\b/i,
                /\brate of rheumatoid arthritis[- ]related outpatient visits?\b/i,
            ],
        },
        {
            id: 'rheumatoid_arthritis_related_physician_office_utilization',
            label: 'RA-Related Physician / Office Visits',
            category: 'economic',
            measureLabel: 'RA-Related Healthcare Resource Utilization',
            thresholdLabel: 'RA-Related Physician / Office Visits',
            patterns: [
                /\brheumatoid arthritis[- ]related physician visits?\b/i,
                /\brheumatoid arthritis[- ]related office visits?\b/i,
                /\brheumatoid arthritis[- ]related physician office visits?\b/i,
                /\brheumatoid arthritis[- ]related primary care visits?\b/i,
                /\brheumatoid arthritis[- ]related specialist visits?\b/i,
                /\brheumatoid arthritis[- ]related rheumatology visits?\b/i,
                /\brheumatoid arthritis[- ]related rheumatologist visits?\b/i,
                /\bRA[- ]related physician visits?\b/i,
                /\bRA[- ]related office visits?\b/i,
                /\bRA[- ]related rheumatology visits?\b/i,
                /\bphysician visits? attributable to rheumatoid arthritis\b/i,
                /\brheumatology visits? attributable to rheumatoid arthritis\b/i,
            ],
        },
        {
            id: 'rheumatoid_arthritis_related_urgent_care_utilization',
            label: 'RA-Related Urgent Care Visits',
            category: 'economic',
            measureLabel: 'RA-Related Healthcare Resource Utilization',
            thresholdLabel: 'RA-Related Urgent Care Visits',
            patterns: [
                /\brheumatoid arthritis[- ]related urgent care visits?\b/i,
                /\brheumatoid arthritis[- ]related urgent care encounters?\b/i,
                /\bRA[- ]related urgent care visits?\b/i,
                /\bRA[- ]related urgent care encounters?\b/i,
                /\burgent care visits? attributable to rheumatoid arthritis\b/i,
            ],
        },
    ],

    asthma: [
        {
            id: 'fev1',
            label: 'FEV1 / forced expiratory volume',
            category: 'clinicalEfficacy',
            patterns: [/\bFEV[- ]?1\b/i, /\bforced expiratory volume\b/i],
        },
        {
            id: 'spirometry',
            label: 'Spirometry',
            category: 'clinicalEfficacy',
            patterns: [/\bspirometry\b/i, /\brespiratory spirometry\b/i],
        },
        {
            id: 'asthma_exacerbations',
            label: 'Asthma exacerbations',
            category: 'clinicalEfficacy',
            patterns: [
                /\basthma exacerbations?\b/i,
                /\bsevere asthma exacerbations?\b/i,
                /\bclinically significant exacerbations?\b/i,
                /\bexacerbation rate\b/i,
                /\bannuali[sz]ed exacerbation rate\b/i,
            ],
        },
        {
            id: 'asthma_control',
            label: 'Asthma control',
            category: 'clinicalEfficacy',
            patterns: [
                /\basthma control\b/i,
                /\bcontrolled asthma\b/i,
                /\buncontrolled asthma\b/i,
                /\bwell[- ]controlled asthma\b/i,
            ],
        },
        {
            id: 'acq',
            label: 'ACQ / Asthma Control Questionnaire',
            category: 'clinicalEfficacy',
            patterns: [
                /\bACQ\b/i,
                /\bACQ[- ]?5\b/i,
                /\bACQ[- ]?6\b/i,
                /\bACQ[- ]?7\b/i,
                /\basthma control questionnaire\b/i,
            ],
        },
        {
            id: 'rescue_treatment',
            label: 'Rescue treatment',
            category: 'treatmentPatterns',
            patterns: [
                /\brescue treatment\b/i,
                /\brescue therapy\b/i,
                /\brescue medication\b/i,
                /\brescue inhaler\b/i,
                /\bshort[- ]acting beta[- ]agonists?\b/i,
                /\bSABA\b/i,
                /\balbuterol\b/i,
                /\bsalbutamol\b/i,
            ],
        },
        {
            id: 'sgrq',
            label: 'SGRQ',
            category: 'humanistic',
            patterns: [
                /\bSGRQ\b/i,
                /\bSGRQ[- ]?C\b/i,
                /\bSt\.?\s*George'?s Respiratory Questionnaire\b/i,
                /\bSaint George'?s Respiratory Questionnaire\b/i,
            ],
        },
        {
            id: 'aqlq',
            label: 'AQLQ',
            category: 'humanistic',
            patterns: [
                /\bAQLQ\b/i,
                /\basthma quality of life questionnaire\b/i,
                /\basthma[- ]specific quality of life questionnaire\b/i,
            ],
        },
        {
            id: 'promis_global_10',
            label: 'PROMIS Global-10',
            category: 'humanistic',
            patterns: [/\bPROMIS[- ]?10\b/i, /\bPROMIS global\b/i, /\bPROMIS global[- ]?10\b/i],
        },
    ],
    atopic_dermatitis: [
        {
            id: 'easi',
            label: 'EASI',
            category: 'clinicalEfficacy',
            measureLabel: 'EASI',
            patterns: [/\bEASI\b(?![- ]?(?:50|75|90)\b)/i, /\bEczema Area and Severity Index\b/i],
        },
        {
            id: 'easi_50',
            label: 'EASI50',
            category: 'clinicalEfficacy',
            measureLabel: 'EASI',
            thresholdLabel: 'EASI50',
            patterns: [
                /\bEASI[- ]?50\b/i,
                /\bEASI\s*50%\s*(?:improvement|reduction|response)\b/i,
                /\b50%\s*(?:improvement|reduction)\s*in\s*(?:the\s+)?EASI\b/i,
                /\b(?:at least|≥|greater than or equal to)\s*50%\s*(?:improvement|reduction)\s*in\s*(?:the\s+)?EASI\b/i,
                /\bachiev(?:e|ed|es|ing)\s+(?:an?\s+)?EASI[- ]?50(?:\s+response)?\b/i,
            ],
        },
        {
            id: 'easi_75',
            label: 'EASI75',
            category: 'clinicalEfficacy',
            measureLabel: 'EASI',
            thresholdLabel: 'EASI75',
            patterns: [
                /\bEASI[- ]?75\b/i,
                /\bEASI\s*75%\s*(?:improvement|reduction|response)\b/i,
                /\b75%\s*(?:improvement|reduction)\s*in\s*(?:the\s+)?EASI\b/i,
                /\b(?:at least|≥|greater than or equal to)\s*75%\s*(?:improvement|reduction)\s*in\s*(?:the\s+)?EASI\b/i,
                /\bachiev(?:e|ed|es|ing)\s+(?:an?\s+)?EASI[- ]?75(?:\s+response)?\b/i,
            ],
        },
        {
            id: 'easi_90',
            label: 'EASI90',
            category: 'clinicalEfficacy',
            measureLabel: 'EASI',
            thresholdLabel: 'EASI90',
            patterns: [
                /\bEASI[- ]?90\b/i,
                /\bEASI\s*90%\s*(?:improvement|reduction|response)\b/i,
                /\b90%\s*(?:improvement|reduction)\s*in\s*(?:the\s+)?EASI\b/i,
                /\b(?:at least|≥|greater than or equal to)\s*90%\s*(?:improvement|reduction)\s*in\s*(?:the\s+)?EASI\b/i,
                /\bachiev(?:e|ed|es|ing)\s+(?:an?\s+)?EASI[- ]?90(?:\s+response)?\b/i,
            ],
        },
        {
            id: 'scorad',
            label: 'SCORAD',
            category: 'clinicalEfficacy',
            patterns: [/\bSCORAD\b/i, /\bSCORAD index\b/i, /\bscoring atopic dermatitis\b/i],
        },
        {
            id: 'iga',
            label: 'IGA',
            category: 'clinicalEfficacy',
            patterns: [/\bIGA\b/i, /\bIGA[- ]?0\/?1\b/i, /\binvestigator'?s? global assessment\b/i],
        },
        {
            id: 'body_surface_area',
            label: 'BSA affected',
            category: 'clinicalEfficacy',
            patterns: [
                /\bBSA\b/i,
                /\b% BSA\b/i,
                /\bbody surface area\b/i,
                /\bpercent body surface area\b/i,
                /\bpercentage of body surface area affected\b/i,
                /\b% of body surface area affected\b/i,
            ],
        },
        {
            id: 'pruritus_nrs',
            label: 'Pruritus NRS',
            category: 'clinicalEfficacy',
            patterns: [/\bPNRS\b/i, /\bpruritus[- ]?NRS\b/i, /\bpruritus numeric(?:al)? rating scale\b/i],
        },
        {
            id: 'wi_nrs',
            label: 'PP-NRS/WI-NRS',
            category: 'clinicalEfficacy',
            patterns: [
                /\bWI[- ]?NRS\b/i,
                /\bWI[- ]?NRS4\b/i,
                /\bWorst Itch N(?:umeric|umerical) Rating Scale\b/i,
                /\bWorst Itch NRS\b/i,
                /\bPP[- ]?NRS\b/i,
                /\bpeak pruritus[- ]?NRS\b/i,
                /\bpeak pruritus numeric(?:al)? rating scale\b/i,
            ],
        },
        {
            id: 'wi_nrs_ge4',
            label: 'WI-NRS ≥4-point improvement',
            category: 'clinicalEfficacy',
            patterns: [
                /\bWI[- ]?NRS.*(?:>=|≥)\s*4\b/i,
                /\b(?:>=|≥)\s*4[- ]?point.*WI[- ]?NRS\b/i,
                /\bWorst Itch NRS 4\b/i,
            ],
        },
        {
            id: 'skin_pain_nrs',
            label: 'Skin Pain NRS',
            category: 'clinicalEfficacy',
            patterns: [/\bSP[- ]?NRS\b/i, /\bskin pain[- ]?NRS\b/i, /\bskin pain numeric(?:al)? rating scale\b/i],
        },
        {
            id: 'sleep_disturbance_nrs',
            label: 'Sleep Disturbance NRS',
            category: 'clinicalEfficacy',
            measureLabel: 'Sleep Disturbance NRS',
            patterns: [
                /\bSD[- ]?NRS\b(?!\s*(?:change|improvement|reduction)?\s*(?:of\s*)?(?:≥|>=|at least)\s*4\b)/i,
                /\bSleep Disturbance(?:\s*\(SD\))?[- ]?NRS\b/i,
                /\bSleep Disturbance Numeric(?:al)? Rating Scale\b/i,
            ],
        },
        {
            id: 'sleep_disturbance_nrs_ge4_point_improvement',
            label: 'SD-NRS ≥4-Point Improvement',
            category: 'clinicalEfficacy',
            measureLabel: 'Sleep Disturbance NRS',
            thresholdLabel: 'SD-NRS ≥4-Point Improvement',
            patterns: [
                /\bSD[- ]?NRS\b.{0,40}\b(?:≥|>=|at least)\s*4[- ]?points?\b/i,
                /\bSD[- ]?NRS\b.{0,40}\b(?:improvement|decrease|reduction)\s+(?:of\s*)?(?:≥|>=|at least)\s*4[- ]?points?\b/i,
                /\b(?:≥|>=|at least)\s*4[- ]?points?\s+(?:improvement|decrease|reduction)\s+in\s+(?:the\s+)?SD[- ]?NRS\b/i,
                /\b(?:improvement|decrease|reduction)\s+(?:of\s*)?(?:≥|>=|at least)\s*4[- ]?points?\s+(?:from baseline\s+)?in\s+(?:the\s+)?SD[- ]?NRS\b/i,
                /\b(?:improvement|decrease|reduction)\s+(?:of\s*)?(?:≥|>=|at least)\s*4[- ]?points?\s+from baseline\s+in\s+(?:the\s+)?Sleep Disturbance(?:\s*\(SD\))?[- ]?NRS\b/i,
                /\bSleep Disturbance(?:\s*\(SD\))?[- ]?NRS\b.{0,40}\b(?:≥|>=|at least)\s*4[- ]?points?\b/i,
                /\bSleep Disturbance Numeric(?:al)? Rating Scale\b.{0,40}\b(?:≥|>=|at least)\s*4[- ]?points?\b/i,
            ],
        },
        {
            id: 'physician_global_assessment',
            label: 'IGA/PhGA',
            category: 'clinicalEfficacy',
            patterns: [/\bPhGA\b/i, /\bphysician'?s? global assessment\b/i, /\bphysicians global assessment\b/i],
        },
        {
            id: 'treatment_success',
            label: 'Treatment success',
            category: 'clinicalEfficacy',
            patterns: [/\btreatment success\b/i, /\boverall treatment success\b/i],
        },
        {
            id: 'dlqi',
            label: 'DLQI',
            category: 'humanistic',
            measureLabel: 'DLQI',
            patterns: [
                /\bDLQI\b(?!\s*(?:score\s*)?(?:[-–—]?\s*0\s*\/\s*1|[-–—]?\s*0\s+(?:or|and)\s+1|(?:≥|>=|at least)\s*4))/i,
                /\bDermatology Life Quality Index\b/i,
            ],
        },
        {
            id: 'dlqi_ge4_point_improvement',
            label: 'DLQI ≥4-Point Improvement',
            category: 'humanistic',
            measureLabel: 'DLQI',
            thresholdLabel: 'DLQI ≥4-Point Improvement',
            patterns: [
                /\bDLQI(?:\s+(?:total\s+)?score)?\b.{0,40}\b(?:≥|>=|at least)\s*4[- ]?points?\b/i,
                /\bDLQI(?:\s+(?:total\s+)?score)?\b.{0,40}\b(?:decreas(?:e|ed)|reduc(?:e|ed|tion)|improv(?:e|ed|ement))\b.{0,20}\b(?:≥|>=|at least)\s*4[- ]?points?\b/i,
                /\b(?:≥|>=|at least)\s*4[- ]?points?\s+(?:decrease|reduction|improvement)\s+in\s+(?:the\s+)?DLQI(?:\s+(?:total\s+)?score)?\b/i,
                /\b(?:decrease|reduction|improvement)\s+of\s+(?:≥|>=|at least)\s*4[- ]?points?\s+in\s+(?:the\s+)?DLQI(?:\s+(?:total\s+)?score)?\b/i,
                /\bDLQI(?:\s+(?:total\s+)?score)?\s+(?:decrease|reduction|improvement)\s+(?:of\s+)?(?:≥|>=|at least)\s*4[- ]?points?\b/i,
                /\bclinically meaningful improvement in\s+(?:the\s+)?DLQI\b/i,
                /\bDLQI\s+(?:minimal clinically important difference|MCID)\b/i,
            ],
        },
        {
            id: 'dlqi_0_1',
            label: 'DLQI 0/1',
            category: 'humanistic',
            measureLabel: 'DLQI',
            thresholdLabel: 'DLQI 0/1',
            patterns: [
                /\bDLQI(?:\s+(?:total\s+)?score)?\s*(?:of\s*)?[-–—]?\s*0\s*\/\s*1\b/i,
                /\bDLQI(?:\s+(?:total\s+)?score)?\s*(?:of\s*)?[-–—]?\s*0\s+(?:or|and)\s+1\b/i,
                /\bDLQI(?:\s+(?:total\s+)?score)?\s*(?:of\s*)?[-–—]?\s*(?:0|1)\b/i,
                /\bachiev(?:e|ed|es|ing)\s+(?:a\s+)?DLQI(?:\s+(?:total\s+)?score)?\s*(?:of\s*)?0\s*\/\s*1\b/i,
                /\bachiev(?:e|ed|es|ing)\s+(?:a\s+)?DLQI(?:\s+(?:total\s+)?score)?\s*(?:of\s*)?0\s+(?:or|and)\s+1\b/i,
                /\bDLQI(?:\s+(?:total\s+)?score)?\s+indicat(?:ing|ive of)\s+(?:no|minimal)\s+(?:effect|impact)\b/i,
            ],
        },
        {
            id: 'cdlqi',
            label: 'CDLQI',
            category: 'humanistic',
            patterns: [
                /\bCDLQI\b/i,
                /\bchildren'?s dermatology life quality index\b/i,
                /\bchildrens dermatology life quality index\b/i,
            ],
        },
        {
            id: 'poem',
            label: 'POEM',
            category: 'humanistic',
            patterns: [/\bPOEM\b/i, /\bPOEM score\b/i, /\bpatient[- ]oriented eczema measure\b/i],
        },
        {
            id: 'sleep_loss_scale',
            label: 'Sleep-Loss Scale',
            category: 'humanistic',
            patterns: [
                /\bsleep[- ]loss scale\b/i,
                /\bsleep[- ]loss rating scale\b/i,
                /\bitch[- ]related sleep[- ]loss scale\b/i,
            ],
        },
        {
            id: 'wpai',
            label: 'WPAI',
            category: 'economic',
            patterns: [/\bWPAI\b/i, /\bWPAI questionnaire\b/i, /\bwork productivity and activity impairment\b/i],
        },
        {
            id: 'atopic_dermatitis_related_healthcare_costs',
            label: 'AD-Related Healthcare Costs',
            category: 'economic',
            measureLabel: 'AD-related Healthcare Costs',
            patterns: [
                /\batopic dermatitis[- ]related health[- ]?care costs?\b/i,
                /\bAD[- ]related health[- ]?care costs?\b/i,
                /\bhealth[- ]?care costs? attributable to atopic dermatitis\b/i,
                /\bdisease[- ]related health[- ]?care costs?\b/i,
            ],
        },
        {
            id: 'atopic_dermatitis_related_medical_costs',
            label: 'AD-related Medical Costs',
            category: 'economic',
            measureLabel: 'AD-related Medical Costs',
            patterns: [
                /\batopic dermatitis[- ]related medical costs?\b/i,
                /\bAD[- ]related medical costs?\b/i,
                /\bmedical costs? attributable to atopic dermatitis\b/i,
            ],
        },
        {
            id: 'atopic_dermatitis_related_inpatient_costs',
            label: 'AD-related Inpatient / Hospitalization Costs',
            category: 'economic',
            measureLabel: 'AD-Related Medical Costs',
            thresholdLabel: 'AD-related Inpatient / Hospitalization Costs',
            patterns: [
                /\batopic dermatitis[- ]related inpatient costs?\b/i,
                /\batopic dermatitis[- ]related hospitali[sz]ation costs?\b/i,
                /\bAD[- ]related hospitali[sz]ation costs?\b/i,
                /\bcosts? of hospitali[sz]ations? attributable to atopic dermatitis\b/i,
            ],
        },
        {
            id: 'atopic_dermatitis_related_pharmacy_costs',
            label: 'AD-related Pharmacy Costs',
            category: 'economic',
            measureLabel: 'AD-related Pharmacy Costs',
            patterns: [
                /\batopic dermatitis[- ]related pharmacy costs?\b/i,
                /\batopic dermatitis[- ]related drug costs?\b/i,
                /\batopic dermatitis[- ]related medication costs?\b/i,
                /\batopic dermatitis[- ]related prescription costs?\b/i,
                /\bAD[- ]related pharmacy costs?\b/i,
                /\bAD[- ]related drug costs?\b/i,
                /\bAD[- ]related medication costs?\b/i,
                /\bpharmacy costs? attributable to atopic dermatitis\b/i,
                /\bdrug costs? attributable to atopic dermatitis\b/i,
                /\bmedication costs? attributable to atopic dermatitis\b/i,
                /\bprescription drug costs? attributable to atopic dermatitis\b/i,
            ],
        },
        {
            id: 'atopic_dermatitis_related_healthcare_resource_utilization',
            label: 'AD-Related Healthcare Resource Utilization',
            category: 'economic',
            measureLabel: 'AD-Related Healthcare Resource Utilization',
            patterns: [
                /\batopic dermatitis[- ]related health[- ]?care resource utili[sz]ation\b/i,
                /\batopic dermatitis[- ]related health[- ]?care utili[sz]ation\b/i,
                /\batopic dermatitis[- ]related medical resource utili[sz]ation\b/i,
                /\batopic dermatitis[- ]related health service utili[sz]ation\b/i,
                /\batopic dermatitis[- ]related HCRU\b/i,
                /\bAD[- ]related health[- ]?care resource utili[sz]ation\b/i,
                /\bAD[- ]related health[- ]?care utili[sz]ation\b/i,
                /\bAD[- ]related HCRU\b/i,
                /\bhealth[- ]?care resource utili[sz]ation attributable to atopic dermatitis\b/i,
                /\bdisease[- ]related health[- ]?care resource utili[sz]ation\b/i,
            ],
        },
        {
            id: 'atopic_dermatitis_related_inpatient_utilization',
            label: 'AD-Related Inpatient Stays / Hospitalizations',
            category: 'economic',
            measureLabel: 'AD-Related Healthcare Resource Utilization',
            thresholdLabel: 'AD-Related Inpatient Stays / Hospitalizations',
            patterns: [
                /\batopic dermatitis[- ]related inpatient stays?\b/i,
                /\batopic dermatitis[- ]related inpatient admissions?\b/i,
                /\batopic dermatitis[- ]related hospital admissions?\b/i,
                /\batopic dermatitis[- ]related hospitali[sz]ations?\b/i,
                /\batopic dermatitis[- ]related hospital stays?\b/i,
                /\bAD[- ]related inpatient stays?\b/i,
                /\bAD[- ]related hospital admissions?\b/i,
                /\bAD[- ]related hospitali[sz]ations?\b/i,
                /\bhospitali[sz]ations? attributable to atopic dermatitis\b/i,
                /\binpatient admissions? attributable to atopic dermatitis\b/i,
                /\bnumber of atopic dermatitis[- ]related hospitali[sz]ations?\b/i,
                /\batopic dermatitis[- ]related inpatient days?\b/i,
                /\batopic dermatitis[- ]related length of stay\b/i,
            ],
        },
        {
            id: 'atopic_dermatitis_related_emergency_department_utilization',
            label: 'AD-Related Emergency Department Visits',
            category: 'economic',
            measureLabel: 'AD-Related Healthcare Resource Utilization',
            thresholdLabel: 'AD-Related Emergency Department Visits',
            patterns: [
                /\batopic dermatitis[- ]related emergency department visits?\b/i,
                /\batopic dermatitis[- ]related emergency room visits?\b/i,
                /\batopic dermatitis[- ]related (?:ED|ER) visits?\b/i,
                /\bAD[- ]related emergency department visits?\b/i,
                /\bAD[- ]related emergency room visits?\b/i,
                /\bAD[- ]related (?:ED|ER) visits?\b/i,
                /\bemergency department visits? attributable to atopic dermatitis\b/i,
                /\bemergency room visits? attributable to atopic dermatitis\b/i,
                /\bnumber of atopic dermatitis[- ]related emergency department visits?\b/i,
            ],
        },
        {
            id: 'atopic_dermatitis_related_outpatient_utilization',
            label: 'AD-Related Outpatient / Ambulatory Care Visits',
            category: 'economic',
            measureLabel: 'AD-Related Healthcare Resource Utilization',
            thresholdLabel: 'AD-Related Outpatient / Ambulatory Care Visits',
            patterns: [
                /\batopic dermatitis[- ]related outpatient visits?\b/i,
                /\batopic dermatitis[- ]related outpatient encounters?\b/i,
                /\batopic dermatitis[- ]related ambulatory care visits?\b/i,
                /\batopic dermatitis[- ]related ambulatory visits?\b/i,
                /\bAD[- ]related outpatient visits?\b/i,
                /\bAD[- ]related outpatient encounters?\b/i,
                /\bAD[- ]related ambulatory care visits?\b/i,
                /\boutpatient visits? attributable to atopic dermatitis\b/i,
                /\bambulatory care visits? attributable to atopic dermatitis\b/i,
                /\bnumber of atopic dermatitis[- ]related outpatient visits?\b/i,
            ],
        },
        {
            id: 'atopic_dermatitis_related_physician_office_utilization',
            label: 'AD-Related Physician / Office Visits',
            category: 'economic',
            measureLabel: 'AD-Related Healthcare Resource Utilization',
            thresholdLabel: 'AD-Related Physician / Office Visits',
            patterns: [
                /\batopic dermatitis[- ]related physician visits?\b/i,
                /\batopic dermatitis[- ]related office visits?\b/i,
                /\batopic dermatitis[- ]related physician office visits?\b/i,
                /\batopic dermatitis[- ]related primary care visits?\b/i,
                /\batopic dermatitis[- ]related specialist visits?\b/i,
                /\batopic dermatitis[- ]related dermatology visits?\b/i,
                /\batopic dermatitis[- ]related dermatologist visits?\b/i,
                /\bAD[- ]related physician visits?\b/i,
                /\bAD[- ]related office visits?\b/i,
                /\bAD[- ]related dermatology visits?\b/i,
                /\bphysician visits? attributable to atopic dermatitis\b/i,
                /\bdermatology visits? attributable to atopic dermatitis\b/i,
            ],
        },
        {
            id: 'atopic_dermatitis_related_urgent_care_utilization',
            label: 'AD-Related Urgent Care Visits',
            category: 'economic',
            measureLabel: 'AD-Related Healthcare Resource Utilization',
            thresholdLabel: 'AD-Related Urgent Care Visits',
            patterns: [
                /\batopic dermatitis[- ]related urgent care visits?\b/i,
                /\batopic dermatitis[- ]related urgent care encounters?\b/i,
                /\bAD[- ]related urgent care visits?\b/i,
                /\bAD[- ]related urgent care encounters?\b/i,
                /\burgent care visits? attributable to atopic dermatitis\b/i,
            ],
        },
    ],
    sjd: [
        {
            id: 'essdai',
            label: 'ESSDAI / EULAR Sjögren’s Syndrome Disease Activity Index',
            category: 'clinicalEfficacy',
            patterns: [
                /\bESSDAI\b/i,
                /\bESSDAI score\b/i,
                /\bEULAR Sj[oö]gren'?s Syndrome Disease Activity Index\b/i,
                /\bEULAR Sj[oö]grens Syndrome Disease Activity Index\b/i,
            ],
        },
        {
            id: 'essdai_mcii',
            label: 'ESSDAI MCII (≥3-point improvement)',
            category: 'clinicalEfficacy',
            patterns: [
                /\b≥\s*3[- ]?point reduction in ESSDAI\b/i,
                /\b3[- ]?point reduction in ESSDAI\b/i,
                /\bimprovement of ≥\s*3 points in ESSDAI\b/i,
                /\bminimal clinically important improvement.*ESSDAI\b/i,
            ],
        },
        {
            id: 'clinessdai',
            label: 'ClinESSDAI',
            category: 'clinicalEfficacy',
            patterns: [
                /\bClinESSDAI\b/i,
                /\bclinical European League Against Rheumatism Sj[oö]gren'?s Syndrome Disease Activity Index\b/i,
            ],
        },
        {
            id: 'esspri',
            label: 'ESSPRI / EULAR Sjögren’s Syndrome Patient Reported Index',
            category: 'clinicalEfficacy',
            patterns: [
                /\bESSPRI\b/i,
                /\bEULAR Sj[oö]gren Syndrome Patient Reported Index\b/i,
                /\bEULAR Sj[oö]gren’?s Syndrome Patient Reported Index\b/i,
                /\bEuropean League Against Rheumatism.*Patient[- ]Reported Index\b/i,
            ],
        },
        {
            id: 'esspri_response',
            label: 'ESSPRI response',
            category: 'clinicalEfficacy',
            patterns: [
                /\bESSPRI\s*≤\s*3\b/i,
                /\bESSPRI ≤ 3\b/i,
                /\b≥\s*1\.5[- ]?point reduction in ESSPRI\b/i,
                /\bESSPRI ≤ 3 AND ≥ 1\.5 points\b/i,
                /\bESSPRI response\b/i,
            ],
        },
        {
            id: 'dissa',
            label: 'DiSSA / Diary of Sjögren’s Symptoms Assessment',
            category: 'clinicalEfficacy',
            patterns: [
                /\bDiSSA\b/i,
                /\bDiary of Sj[oö]gren'?s Symptoms Assessment\b/i,
                /\bdisease activity score for Sj[oö]gren'?s\b/i,
            ],
        },
        {
            id: 'dissa_sicca',
            label: 'DiSSA sicca domain',
            category: 'clinicalEfficacy',
            patterns: [/\bDiSSA sicca\b/i, /\bsicca domain\b/i],
        },
        {
            id: 'salivary_flow',
            label: 'Salivary flow',
            category: 'clinicalEfficacy',
            patterns: [
                /\bsalivary flow\b/i,
                /\bstimulated salivary flow\b/i,
                /\bstimulated salivary flow rate\b/i,
                /\bunstimulated salivary flow\b/i,
                /\bunstimulated whole salivary flow\b/i,
                /\bwhole unstimulated sialometry\b/i,
                /\bwhole stimulated sialometry\b/i,
                /\bUWS\b/i,
                /\bUWS flow\b/i,
            ],
        },
        {
            id: 'bcva',
            label: 'BCVA / Best corrected visual acuity',
            category: 'clinicalEfficacy',
            patterns: [/\bBCVA\b/i, /\bbest[- ]corrected visual acuity\b/i, /\bbest corrected visual acuity\b/i],
        },
        {
            id: 'sande',
            label: 'SANDE / Symptom Assessment in Dry Eye',
            category: 'clinicalEfficacy',
            patterns: [/\bSANDE\b/i, /\bsymptom assessment in dry eye\b/i],
        },
        {
            id: 'sssd',
            label: 'Sjögren’s Syndrome Symptom Diary',
            category: 'humanistic',
            patterns: [
                /\bSSSD\b/i,
                /\bSj[oö]gren'?s Syndrome Symptom Diary\b/i,
                /\bSj[oö]gren’s Syndrome Symptom Diary\b/i,
            ],
        },
        {
            id: 'fatigue',
            label: 'Fatigue',
            category: 'humanistic',
            patterns: [/\bfatigue\b/i, /\bfatigue severity\b/i, /\bfatigue symptoms\b/i],
        },
        {
            id: 'dryness_score',
            label: 'Dryness score',
            category: 'humanistic',
            patterns: [
                /\bdryness score\b/i,
                /\bSj[oö]gren'?s symptoms dryness score\b/i,
                /\bocular dryness\b/i,
                /\boral dryness\b/i,
                /\bmouth dryness\b/i,
                /\beye dryness\b/i,
            ],
        },
        {
            id: 'joint_pain_score',
            label: 'Joint pain score',
            category: 'humanistic',
            patterns: [
                /\bjoint pain score\b/i,
                /\bSj[oö]gren'?s symptoms joint pain score\b/i,
                /\barthralgia score\b/i,
            ],
        },
    ],
    chronic_spontaneous_urticaria: [
        // =========================
        // Clinical efficacy
        // =========================
        {
            id: 'uas7',
            label: 'UAS7 / Urticaria Activity Score',
            category: 'clinicalEfficacy',
            patterns: [
                /\bUAS[- ]?7\b/i,
                /\b7[- ]day urticaria activity score\b/i,
                /\burticaria activity score over 7 days\b/i,
                /\bweekly urticaria activity score\b/i,
            ],
        },
        {
            id: 'uas7_complete_response',
            label: 'UAS7 complete response',
            category: 'clinicalEfficacy',
            patterns: [
                /\bUAS[- ]?7\s*=\s*0\b/i,
                /\bachiev(?:e|ing) complete control \(?UAS[- ]?7\s*=\s*0\)?\b/i,
                /\bcomplete control \(?UAS[- ]?7\s*=\s*0\)?\b/i,
                /\bcomplete responders? \(?UAS[- ]?7\s*=\s*0\)?\b/i,
            ],
        },
        {
            id: 'uas7_well_controlled',
            label: 'UAS7 well-controlled urticaria',
            category: 'clinicalEfficacy',
            patterns: [
                /\bUAS[- ]?7\s*(?:<=|≤)\s*6\b/i,
                /\bwell[- ]controlled urticaria \(?UAS[- ]?7\s*(?:<=|≤)\s*6\)?\b/i,
            ],
        },
        {
            id: 'iss7',
            label: 'ISS7 / Itch Severity Score',
            category: 'clinicalEfficacy',
            patterns: [
                /\bISS[- ]?7\b/i,
                /\bitch severity score 7\b/i,
                /\bitch severity score over 7 consecutive days\b/i,
                /\bweekly itch severity score\b/i,
                /\bsum of the preceding 7 days'? itch scores\b/i,
            ],
        },
        {
            id: 'iss7_complete_response',
            label: 'ISS7 complete response',
            category: 'clinicalEfficacy',
            patterns: [/\bISS[- ]?7\s*=\s*0\b/i],
        },
        {
            id: 'hss7',
            label: 'HSS7 / Hives Severity Score',
            category: 'clinicalEfficacy',
            patterns: [
                /\bHSS[- ]?7\b/i,
                /\bhive severity score over 7 consecutive days\b/i,
                /\bhives severity score 7\b/i,
                /\bhives severity score over 7 days\b/i,
            ],
        },
        {
            id: 'hss7_complete_response',
            label: 'HSS7 complete response',
            category: 'clinicalEfficacy',
            patterns: [/\bHSS[- ]?7\s*=\s*0\b/i],
        },
        {
            id: 'aas7',
            label: 'AAS7 / Angioedema Activity Score',
            category: 'clinicalEfficacy',
            patterns: [
                /\bAAS[- ]?7\b/i,
                /\bangioedema activity score over 7 consecutive days\b/i,
                /\bangioedema activity score used for 7 consecutive days\b/i,
            ],
        },
        {
            id: 'uct',
            label: 'UCT / Urticaria Control Test',
            category: 'clinicalEfficacy',
            patterns: [/\bUCT\b/i, /\burticaria control test\b/i],
        },
        {
            id: 'uct_complete_control',
            label: 'UCT complete control',
            category: 'clinicalEfficacy',
            patterns: [/\bUCT\s*=\s*16\b/i, /\bcomplete controls? \(?UCT\s*=\s*16\)?\b/i],
        },
        {
            id: 'csu_complete_control',
            label: 'Complete control',
            category: 'clinicalEfficacy',
            patterns: [
                /\bcomplete control\b/i,
                /\bcomplete controlled disease\b/i,
                /\bcomplete absence of hives and itch\b/i,
                /\bUAS[- ]?7\s*=\s*0\s*(?:and|,)?\s*AAS[- ]?7\s*=\s*0\b/i,
            ],
        },
        {
            id: 'csu_loss_of_control',
            label: 'Loss of control',
            category: 'clinicalEfficacy',
            patterns: [
                /\bloss of control\b/i,
                /\bloss of complete control\b/i,
                /\bloss of complete controlled disease\b/i,
                /\btime to loss of complete controlled disease\b/i,
            ],
        },
        {
            id: 'dlqi',
            label: 'DLQI',
            category: 'humanistic',
            measureLabel: 'DLQI',
            patterns: [
                /\bDLQI\b(?!\s*(?:score\s*)?(?:[-–—]?\s*0\s*\/\s*1|[-–—]?\s*0\s+(?:or|and)\s+1|(?:≥|>=|at least)\s*4))/i,
                /\bDermatology Life Quality Index\b/i,
            ],
        },
        {
            id: 'dlqi_ge4_point_improvement',
            label: 'DLQI ≥4-Point Improvement',
            category: 'humanistic',
            measureLabel: 'DLQI',
            thresholdLabel: 'DLQI ≥4-Point Improvement',
            patterns: [
                /\bDLQI(?:\s+(?:total\s+)?score)?\b.{0,40}\b(?:≥|>=|at least)\s*4[- ]?points?\b/i,
                /\bDLQI(?:\s+(?:total\s+)?score)?\b.{0,40}\b(?:decreas(?:e|ed)|reduc(?:e|ed|tion)|improv(?:e|ed|ement))\b.{0,20}\b(?:≥|>=|at least)\s*4[- ]?points?\b/i,
                /\b(?:≥|>=|at least)\s*4[- ]?points?\s+(?:decrease|reduction|improvement)\s+in\s+(?:the\s+)?DLQI(?:\s+(?:total\s+)?score)?\b/i,
                /\b(?:decrease|reduction|improvement)\s+of\s+(?:≥|>=|at least)\s*4[- ]?points?\s+in\s+(?:the\s+)?DLQI(?:\s+(?:total\s+)?score)?\b/i,
                /\bDLQI(?:\s+(?:total\s+)?score)?\s+(?:decrease|reduction|improvement)\s+(?:of\s+)?(?:≥|>=|at least)\s*4[- ]?points?\b/i,
                /\bclinically meaningful improvement in\s+(?:the\s+)?DLQI\b/i,
                /\bDLQI\s+(?:minimal clinically important difference|MCID)\b/i,
            ],
        },
        {
            id: 'dlqi_0_1',
            label: 'DLQI 0/1',
            category: 'humanistic',
            measureLabel: 'DLQI',
            thresholdLabel: 'DLQI 0/1',
            patterns: [
                /\bDLQI(?:\s+(?:total\s+)?score)?\s*(?:of\s*)?[-–—]?\s*0\s*\/\s*1\b/i,
                /\bDLQI(?:\s+(?:total\s+)?score)?\s*(?:of\s*)?[-–—]?\s*0\s+(?:or|and)\s+1\b/i,
                /\bDLQI(?:\s+(?:total\s+)?score)?\s*(?:of\s*)?[-–—]?\s*(?:0|1)\b/i,
                /\bachiev(?:e|ed|es|ing)\s+(?:a\s+)?DLQI(?:\s+(?:total\s+)?score)?\s*(?:of\s*)?0\s*\/\s*1\b/i,
                /\bachiev(?:e|ed|es|ing)\s+(?:a\s+)?DLQI(?:\s+(?:total\s+)?score)?\s*(?:of\s*)?0\s+(?:or|and)\s+1\b/i,
                /\bDLQI(?:\s+(?:total\s+)?score)?\s+indicat(?:ing|ive of)\s+(?:no|minimal)\s+(?:effect|impact)\b/i,
            ],
        },
    ],
    eosinophilic_esophagitis: [
        {
            id: 'erefs',
            label: 'EREFS / EoE Endoscopic Reference Score',
            category: 'clinicalEfficacy',
            patterns: [
                /\bEREFS\b/i,
                /\bEoE[- ]?EREFS\b/i,
                /\bEoE Endoscopic Reference Score\b/i,
                /\bEosinophilic Esophagitis Endoscopic Reference Score\b/i,
                /\beosinophilic esophagitis[- ]endoscopic reference score\b/i,
            ],
        },
        {
            id: 'erefs_inflammatory',
            label: 'EREFS inflammatory subscore',
            category: 'clinicalEfficacy',
            patterns: [/\bEREFS inflammatory subscore\b/i],
        },
        {
            id: 'peak_esophageal_eosinophil_count',
            label: 'Peak esophageal eosinophil count',
            category: 'clinicalEfficacy',
            patterns: [
                /\bpeak eosinophil count\b/i,
                /\bpeak esophageal eosinophil count\b/i,
                /\bpeak esophageal intraepithelial eosinophil count\b/i,
                /\bpeak esophageal intraepithelial eosinophils?\b/i,
                /\bPEC\b/i,
                /\beos(?:inophils)?\/hpf\b/i,
                /\beosinophils? per high[- ]power field\b/i,
            ],
        },
        {
            id: 'peak_esophageal_eosinophil_count_le_6',
            label: 'Peak esophageal eosinophil count ≤6 eos/hpf',
            category: 'clinicalEfficacy',
            patterns: [
                /\bpeak esophageal.*eosinophil.*(?:<=|≤)\s*6\b/i,
                /\bpeak esophageal.*eos\/hpf.*(?:<=|≤)\s*6\b/i,
                /\b(?:<=|≤)\s*6 eosinophils?\/high power field\b/i,
                /\b(?:<=|≤)\s*6 eos(?:inophils)?\/hpf\b/i,
            ],
        },
        {
            id: 'peak_esophageal_eosinophil_count_lt_15',
            label: 'Peak esophageal eosinophil count <15 eos/hpf',
            category: 'clinicalEfficacy',
            patterns: [
                /\bpeak esophageal.*eosinophil.*(?:<|<=|≤)\s*15\b/i,
                /\bpeak esophageal.*eos\/hpf.*(?:<|<=|≤)\s*15\b/i,
                /\b(?:<|<=|≤)\s*15 eosinophils?\/high power field\b/i,
                /\b(?:<|<=|≤)\s*15 eos(?:inophils)?\/hpf\b/i,
            ],
        },
        {
            id: 'eoe_hss',
            label: 'EoE-HSS / Histology Scoring System',
            category: 'clinicalEfficacy',
            patterns: [
                /\bEoE[- ]?HSS\b/i,
                /\bEoE[- ]?HSS stage\b/i,
                /\bEoE[- ]?HSS grade\b/i,
                /\bEosinophilic Esophagitis Histology Scoring System\b/i,
                /\bhistologic scoring system\b/i,
            ],
        },
        {
            id: 'dsq',
            label: 'DSQ / Dysphagia Symptom Questionnaire',
            category: 'humanistic',
            patterns: [/\bDSQ\b/i, /\bDSQ score\b/i, /\bDysphagia Symptom Questionnaire\b/i],
        },
        {
            id: 'dysphagia_days',
            label: 'Dysphagia days',
            category: 'humanistic',
            patterns: [/\bdysphagia days\b/i],
        },
        {
            id: 'peess',
            label: 'PEESS / Pediatric EoE Symptom Scores',
            category: 'humanistic',
            patterns: [/\bPEESS\b/i, /\bPediatric Eosinophilic Esophagitis Symptom Scores?\b/i],
        },
        {
            id: 'eoe_iq',
            label: 'EoE-IQ / EoE Impact Questionnaire',
            category: 'humanistic',
            patterns: [
                /\bEoE[- ]?IQ\b/i,
                /\bEosinophilic Esophagitis[- ]Impact Questionnaire\b/i,
                /\bEosinophilic Esophagitis Impact Questionnaire\b/i,
            ],
        },
        {
            id: 'straumann_dysphagia_index',
            label: 'Straumann Dysphagia Index',
            category: 'humanistic',
            patterns: [/\bSDI\b/i, /\bStraumann Dysphagia Index\b/i],
        },
    ],
    major_depressive_disorder: [
        {
            id: 'madrs',
            label: 'MADRS / Montgomery-Åsberg Depression Rating Scale',
            category: 'clinicalEfficacy',
            patterns: [
                /\bMADRS\b/i,
                /\bMontgomery[- ](?:Åsberg|Asberg|Aasberg|Eisberg) Depression Rating Scale\b/i,
                /\bMontgomery (?:and )?(?:Åsberg|Asberg|Aasberg|Eisberg) Depression Rating Scale\b/i,
                /\bMontgomery[- ](?:Åsberg|Asberg) Depression Scale\b/i,
            ],
        },
        {
            id: 'ham_d',
            label: 'HAM-D / Hamilton Depression Rating Scale',
            category: 'clinicalEfficacy',
            patterns: [
                /\bHAM[- ]?D\b/i,
                /\bHAMD\b/i,
                /\bHamilton Depression Rating Scale\b/i,
                /\bHamilton Rating Scale for Depression\b/i,
            ],
        },
        {
            id: 'ham_d17',
            label: 'HAM-D17',
            category: 'clinicalEfficacy',
            patterns: [
                /\bHAM[- ]?D17\b/i,
                /\bHamilton Depression Rating Scale[- ]17 items\b/i,
                /\b17[- ]item Hamilton Depression Rating Scale\b/i,
            ],
        },
        {
            id: 'ham_a',
            label: 'HAM-A / Hamilton Anxiety Rating Scale',
            category: 'clinicalEfficacy',
            patterns: [
                /\bHAM[- ]?A\b/i,
                /\bHAMA\b/i,
                /\bHamilton Anxiety Rating Scale\b/i,
                /\bHamilton Anxiety Scale\b/i,
            ],
        },
        {
            id: 'dars',
            label: 'DARS / Dimensional Anhedonia Rating Scale',
            category: 'clinicalEfficacy',
            patterns: [/\bDARS\b/i, /\bDimensional Anhedonia Rating Scale\b/i],
        },
        {
            id: 'anhedonia',
            label: 'Anhedonia',
            category: 'clinicalEfficacy',
            patterns: [/\banhedonia\b/i, /\banhedonic\b/i],
        },
        {
            id: 'suicidal_ideation',
            label: 'Suicidal ideation',
            category: 'safety',
            patterns: [
                /\bsuicidal ideation\b/i,
                /\bsuicidality\b/i,
                /\bsuicidal thoughts?\b/i,
                /\bsuicidal behavior\b/i,
            ],
        },
        {
            id: 'c_ssrs',
            label: 'C-SSRS / Columbia-Suicide Severity Rating Scale',
            category: 'safety',
            patterns: [/\bC[- ]?SSRS\b/i, /\bColumbia[- ]Suicide Severity Rating Scale\b/i],
        },
        {
            id: 'phq_9',
            label: 'PHQ-9 / Patient Health Questionnaire-9',
            category: 'humanistic',
            patterns: [
                /\bPHQ[- ]?9\b/i,
                /\bPHQ9\b/i,
                /\bPatient Health Questionnaire[- ]9\b/i,
                /\bPatient Health Questionnaire 9[- ]item\b/i,
                /\bPatient Health Questionnaire 9\b/i,
            ],
        },
        {
            id: 'bdi',
            label: 'BDI / Beck Depression Inventory',
            category: 'humanistic',
            patterns: [/\bBDI\b/i, /\bBeck Depression Inventory\b/i],
        },
        {
            id: 'bdi_ii',
            label: 'BDI-II / Beck Depression Inventory-II',
            category: 'humanistic',
            patterns: [/\bBDI[- ]?II\b/i, /\bBeck Depression Inventory[- ](?:2nd Ed|Second Edition|II)\b/i],
        },

        {
            id: 'panss',
            label: 'PANSS / Positive and Negative Syndrome Scale',
            category: 'clinicalEfficacy',
            patterns: [/\bPANSS\b/i, /\bPANSS total score\b/i, /\bPositive (?:and|&) Negative Syndrome Scale\b/i],
        },
    ],
    generalized_anxiety_disorder: [
        {
            id: 'ham_a',
            label: 'HAM-A / Hamilton Anxiety Rating Scale',
            category: 'clinicalEfficacy',
            patterns: [
                /\bHAM[- ]?A\b/i,
                /\bHAMA\b/i,
                /\bHamilton Anxiety Rating Scale\b/i,
                /\bHamilton Anxiety Scale\b/i,
            ],
        },
        {
            id: 'gad_7',
            label: 'GAD-7 / Generalized Anxiety Disorder-7',
            category: 'humanistic',
            patterns: [
                /\bGAD[- ]?7\b/i,
                /\bgad7\b/i,
                /\bgeneralized anxiety disorder[- ]?7\b/i,
                /\bgeneralised anxiety disorder[- ]?7\b/i,
            ],
        },
        {
            id: 'c_ssrs',
            label: 'C-SSRS / Columbia-Suicide Severity Rating Scale',
            category: 'safety',
            patterns: [
                /\bC[- ]?SSRS\b/i,
                /\bColumbia[- ]Suicide Severity Rating Scale\b/i,
                /\bColumbia Suicide Severity Rating Scale\b/i,
            ],
        },
        {
            id: 'suicidal_ideation',
            label: 'Suicidal ideation',
            category: 'safety',
            patterns: [
                /\bsuicidal ideation\b/i,
                /\bsuicidality\b/i,
                /\bsuicidal thoughts?\b/i,
                /\bsuicidal behavior\b/i,
                /\bsuicide risk\b/i,
            ],
        },
    ],
    fabry_disease: [
        {
            id: 'fphpq',
            label: 'FPHPQ / Fabry-specific Pediatric Health and Pain Questionnaire',
            category: 'humanistic',
            patterns: [
                /\bFPHPQ\b/i,
                /\bFabry[- ]specific Pediatric Health and Pain Questionnaire\b/i,
                /\bFabry specific Pediatric Health and Pain Questionnaire\b/i,
            ],
        },
        {
            id: 'fabpro_gi',
            label: 'FABPRO-GI / Fabry Disease PRO Gastrointestinal',
            category: 'humanistic',
            patterns: [
                /\bFABPRO\b/i,
                /\bFABPRO[- ]?GI\b/i,
                /\bFABry Disease Patient[- ]Reported Outcome[- ]Gastrointestinal\b/i,
                /\bFabry Disease PRO Gastrointestinal\b/i,
            ],
        },
        {
            id: 'fd_pro',
            label: 'FD-PRO / Fabry Disease PRO',
            category: 'humanistic',
            patterns: [/\bFD[- ]?PRO\b/i, /\bFabry Disease PRO\b/i],
        },
        {
            id: 'mssi',
            label: 'MSSI / Mainz Severity Score Index',
            category: 'clinicalEfficacy',
            patterns: [/\bMSSI\b/i, /\bMainz Severity Score Index\b/i],
        },
        {
            id: 'pedsql',
            label: 'PedsQL / Pediatric Quality of Life Inventory',
            category: 'humanistic',
            patterns: [
                /\bPedsQL\b/i,
                /\bPedsQL™\b/i,
                /\bPediatric Quality of Life Inventory\b/i,
                /\bPediatric Quality of Life\b/i,
            ],
        },
        {
            id: 'pedsql_ppq',
            label: 'PedsQL Pediatric Pain Questionnaire',
            category: 'humanistic',
            patterns: [
                /\bPedsQL[- ]?PPQ\b/i,
                /\bPediatric Quality of Life Inventory.*Pediatric Pain Questionnaire\b/i,
                /\bPedsQL Pediatric Pain Questionnaire\b/i,
            ],
        },
        {
            id: 'pedsql_gi',
            label: 'PedsQL-GI',
            category: 'humanistic',
            patterns: [/\bPedsQL[- ]?GI\b/i, /\bPedsQL GI\b/i],
        },
        {
            id: 'bpi',
            label: 'BPI / Brief Pain Inventory',
            category: 'humanistic',
            patterns: [/\bBPI\b/i, /\bBrief Pain Inventory\b/i],
        },
        {
            id: 'pain_nrs',
            label: 'Pain numeric rating scale',
            category: 'humanistic',
            patterns: [
                /\bpain NRS\b/i,
                /\bpain numerical rating scale\b/i,
                /\bpain numeric rating scale\b/i,
                /\bpain score\b/i,
            ],
        },
        {
            id: 'lvmi',
            label: 'LVMi / Left Ventricular Mass Index',
            category: 'clinicalEfficacy',
            patterns: [/\bLVMi\b/i, /\bLeft Ventricular Mass Index\b/i],
        },
        {
            id: 'gsrs',
            label: 'GSRS / Gastrointestinal Symptom Rating Scale',
            category: 'humanistic',
            patterns: [/\bGSRS\b/i, /\bGastrointestinal Symptom Rating Scale\b/i],
        },
        {
            id: 'rescue_pain_medication',
            label: 'Rescue pain medication use',
            category: 'treatmentPatterns',
            patterns: [
                /\brescue pain medication\b/i,
                /\brescue pain medication use\b/i,
                /\bfrequency of rescue pain medication\b/i,
            ],
        },
    ],
    rett_syndrome: [
        {
            id: 'csbs_dp',
            label: 'CSBS-DP / Communication and Symbolic Behavior Scales',
            category: 'humanistic',
            patterns: [
                /\bCSBS[- ]?DP\b/i,
                /\bCommunication and Symbolic Behavior Scales Developmental\b/i,
                /\bCommunication and Symbolic Behaviour Scales Developmental\b/i,
            ],
        },
        {
            id: 'icnd',
            label: 'ICND / Childhood Neurologic Disability',
            category: 'clinicalEfficacy',
            patterns: [/\bICND\b/i, /\bChildhood Neurologic Disability\b/i, /\bChildhood Neurological Disability\b/i],
        },
        {
            id: 'rsbq',
            label: 'RSBQ / Rett Syndrome Behaviour Questionnaire',
            category: 'humanistic',
            patterns: [/\bRSBQ\b/i, /\bRett Syndrome Behaviou?r Questionnaire\b/i],
        },
        {
            id: 'rtt_cbi',
            label: 'RTT-CBI / Rett Syndrome Caregiver Burden Inventory',
            category: 'humanistic',
            patterns: [/\bRTT[- ]?CBI\b/i, /\bRett Syndrome Caregiver Burden Inventory\b/i],
        },
        {
            id: 'rtt_comc',
            label: 'RTT-COMC / Rett communication composite',
            category: 'humanistic',
            patterns: [
                /\bRTT[- ]?COMC\b/i,
                /\bRett communication composite\b/i,
                /\bRett Syndrome communication composite\b/i,
            ],
        },
        {
            id: 'rtt_hf',
            label: 'RTT-HF / Rett hand function',
            category: 'clinicalEfficacy',
            patterns: [/\bRTT[- ]?HF\b/i, /\bRett hand function\b/i, /\bRett Syndrome hand function\b/i],
        },
        {
            id: 'rtt_vcom',
            label: 'RTT-VCOM / Rett verbal communication',
            category: 'humanistic',
            patterns: [
                /\bRTT[- ]?VCOM\b/i,
                /\bRett verbal communication\b/i,
                /\bRett Syndrome verbal communication\b/i,
            ],
        },
        {
            id: 'observer_reported_communication_ability',
            label: 'Observer-Reported Communication Ability',
            category: 'humanistic',
            patterns: [
                /\bobserver[- ]reported communication ability\b/i,
                /\bobserver reported communication ability\b/i,
            ],
        },
        {
            id: 'cagi_c',
            label: 'CaGI-C / Caregiver Global Impression of Change',
            category: 'humanistic',
            patterns: [/\bCaGI[- ]?C\b/i, /\bCaregiver Global Impression of Change\b/i],
        },
        {
            id: 'cms_28',
            label: 'CMS-28',
            category: 'clinicalEfficacy',
            patterns: [/\bCMS[- ]?28\b/i],
        },
    ],
    fus_als: [
        {
            id: 'alsfrs',
            label: 'ALSFRS / ALS Functional Rating Scale',
            category: 'clinicalEfficacy',
            patterns: [
                /\bALSFRS\b/i,
                /\bALSFRS score\b/i,
                /\bALS Functional Rating Scale\b/i,
                /\bAmyotrophic Lateral Sclerosis Functional Rating Scale\b/i,
            ],
        },
        {
            id: 'alsaq_5',
            label: 'ALSAQ-5 / ALS Assessment Questionnaire',
            category: 'humanistic',
            patterns: [
                /\bALSAQ[- ]?5\b/i,
                /\bALS Assessment Questionnaire 5[- ]item\b/i,
                /\bAmyotrophic Lateral Sclerosis Assessment Questionnaire[- ]5 items\b/i,
                /\bAmyotrophic Lateral Sclerosis Assessment Scales[- ]5 items\b/i,
            ],
        },
        {
            id: 'vafs',
            label: 'VAFS / Ventilation Assistance-Free Survival',
            category: 'clinicalEfficacy',
            patterns: [/\bVAFS\b/i, /\bVentilation Assistance[- ]Free Survival\b/i],
        },
        {
            id: 'slow_vital_capacity',
            label: 'Slow vital capacity',
            category: 'clinicalEfficacy',
            patterns: [
                /\bSVC\b/i,
                /\bSlow Vital Capacity\b/i,
                /\bslow vital capacity measurements?\b/i,
                /\bpercent predicted normal\b/i,
                /\bpercent of predicted normal\b/i,
            ],
        },
        {
            id: 'hand_held_dynamometry',
            label: 'Hand-held dynamometry',
            category: 'clinicalEfficacy',
            patterns: [
                /\bHHD\b/i,
                /\bHand[- ]held dynamometry\b/i,
                /\bHandheld Dynamometry\b/i,
                /\bhand grip strength.*hand[- ]held dynamometry\b/i,
                /\bmuscle strength.*hand[- ]held dynamometry\b/i,
            ],
        },
    ],
    phenylketonuria: [
        {
            id: 'wisc',
            label: 'WISC / Wechsler Intelligence Scale for Children',
            category: 'clinicalEfficacy',
            patterns: [/\bWISC\b/i, /\bWISC score\b/i, /\bWechsler Intelligence Scale for Children\b/i],
        },
        {
            id: 'fsiq_wppsi',
            label: 'FSIQ-WPPSI',
            category: 'clinicalEfficacy',
            patterns: [
                /\bFSIQ[- ]?WPPSI\b/i,
                /\bFull[- ]scale Intelligence Quotient\b/i,
                /\bFull Scale Intelligence Quotient WPPSI[- ]?IV\b/i,
                /\bWechsler Preschool and Primary Scale of Intelligence\b/i,
            ],
        },
        {
            id: 'pku_qol',
            label: 'PKU-QOL',
            category: 'humanistic',
            patterns: [
                /\bPKU[- ]?QOL\b/i,
                /\bPKU[- ]?QOL questionnaire\b/i,
                /\bPhenylketonuria Quality of Life questionnaire\b/i,
            ],
        },
        {
            id: 'blood_phe',
            label: 'Blood phenylalanine levels',
            category: 'clinicalEfficacy',
            patterns: [/\bblood phenylalanine\b/i, /\bblood Phe\b/i, /\bblood Phe concentration\b/i],
        },
        {
            id: 'plasma_phe',
            label: 'Plasma phenylalanine levels',
            category: 'clinicalEfficacy',
            patterns: [/\bplasma phenylalanine\b/i, /\bplasma Phe\b/i, /\bplasma Phe concentration\b/i],
        },
        {
            id: 'phe_target_360',
            label: 'Phe target <360 μmol/L',
            category: 'clinicalEfficacy',
            patterns: [
                /\bPhe\s*<\s*360\s*(?:μmol\/L|umol\/L|micromol\/L)\b/i,
                /\bphenylalanine\s*<\s*360\s*(?:μmol\/L|umol\/L|micromol\/L)\b/i,
            ],
        },
        {
            id: 'phe_target_600',
            label: 'Phe target <600 μmol/L',
            category: 'clinicalEfficacy',
            patterns: [
                /\bPhe\s*<\s*600\s*(?:μmol\/L|umol\/L|micromol\/L)\b/i,
                /\bphenylalanine\s*<\s*600\s*(?:μmol\/L|umol\/L|micromol\/L)\b/i,
            ],
        },
        {
            id: 'dietary_phe_intake',
            label: 'Dietary phenylalanine intake',
            category: 'treatmentPatterns',
            patterns: [/\bdietary phenylalanine intake\b/i, /\bdietary Phe intake\b/i],
        },
        {
            id: 'dietary_protein_intake',
            label: 'Dietary protein intake',
            category: 'treatmentPatterns',
            patterns: [/\bdietary protein intake\b/i, /\btotal protein intake\b/i],
        },
    ],
    igan: [
        {
            id: 'upcr',
            label: 'UPCR / urine protein-to-creatinine ratio',
            category: 'clinicalEfficacy',
            patterns: [
                /\bUPCR\b/i,
                /\buPCR\b/i,
                /\burine protein[- ]to[- ]creatinine ratio\b/i,
                /\burine protein\/creatinine ratio\b/i,
            ],
        },
        {
            id: 'uacr',
            label: 'UACR / urine albumin-to-creatinine ratio',
            category: 'clinicalEfficacy',
            patterns: [
                /\bUACR\b/i,
                /\burine albumin[- ]to[- ]creatinine ratio\b/i,
                /\burine albumin creatinine ratio\b/i,
            ],
        },
        {
            id: 'egfr',
            label: 'eGFR / estimated glomerular filtration rate',
            category: 'clinicalEfficacy',
            patterns: [
                /\beGFR\b/i,
                /\bestimated GFR\b/i,
                /\bestimated glomerular filtration rate\b/i,
                /\bglomerular filtration rate\b/i,
            ],
        },
        {
            id: 'proteinuria_24h',
            label: '24-hour proteinuria',
            category: 'clinicalEfficacy',
            patterns: [
                /\b24[- ]?hour proteinuria\b/i,
                /\b24[- ]?hour urinary protein\b/i,
                /\b24[- ]?hour urine protein\b/i,
                /\b24\s*h[- ]?UPE\b/i,
            ],
        },
        {
            id: 'serum_creatinine',
            label: 'Serum creatinine',
            category: 'clinicalEfficacy',
            patterns: [/\bSCr\b/i, /\bserum creatinine\b/i, /\bcreatinine levels\b/i],
        },
        {
            id: 'hematuria',
            label: 'Hematuria',
            category: 'clinicalEfficacy',
            patterns: [/\bhematuria\b/i, /\bhaematuria\b/i],
        },
        {
            id: 'mest_c',
            label: 'MEST-C / Oxford classification',
            category: 'clinicalEfficacy',
            patterns: [
                /\bMEST[- ]?C\b/i,
                /\bMEST[- ]?C score\b/i,
                /\bOxford MEST[- ]?C classification\b/i,
                /\bOxford classification\b/i,
            ],
        },
        {
            id: 'composite_kidney_failure_endpoint',
            label: 'Composite kidney failure endpoint',
            category: 'clinicalEfficacy',
            patterns: [
                /\bcomposite endpoint of kidney failure\b/i,
                /\bcomposite kidney failure endpoint\b/i,
                /\btime to kidney failure event\b/i,
                /\btime to progression of CKD\b/i,
                /\bprogression of chronic kidney disease\b/i,
                /\bsustained 40% decline in eGFR\b/i,
            ],
        },
        {
            id: 'renal_response',
            label: 'Renal response',
            category: 'clinicalEfficacy',
            patterns: [
                /\brenal response\b/i,
                /\bcomplete renal response\b/i,
                /\bpartial renal response\b/i,
                /\bclinical remission\b/i,
                /\bpartial remission\b/i,
            ],
        },
        {
            id: 'complement_biomarkers',
            label: 'Complement biomarkers',
            category: 'clinicalEfficacy',
            patterns: [
                /\bcomplement biomarkers?\b/i,
                /\balternative pathway activity\b/i,
                /\bclassical pathway activity\b/i,
                /\bComplement Factor B\b/i,
                /\bBa and Bb fragments\b/i,
                /\bsC5b[- ]?9\b/i,
                /\bC3\b/i,
                /\bC4\b/i,
                /\bCH50\b/i,
            ],
        },
        {
            id: 'gd_iga1',
            label: 'Galactose-deficient IgA1',
            category: 'clinicalEfficacy',
            patterns: [/\bgalactose[- ]deficient IgA1\b/i, /\bGd[- ]?IgA1\b/i, /\banti[- ]Gd[- ]?IgA1 antibodies\b/i],
        },
        {
            id: 'qlq',
            label: 'Quality of Life Questionnaire',
            category: 'humanistic',
            patterns: [/\bQLQ\b/i, /\bQuality of Life Questionnaire\b/i],
        },
    ],
    crswnp: [
        {
            id: 'nasal_polyp_score',
            label: 'Nasal polyp score',
            category: 'clinicalEfficacy',
            patterns: [
                /\bnasal polyp score\b/i,
                /\bnasal polyp scores\b/i,
                /\bNPS\b/i,
                /\bbilateral nasal polyp score\b/i,
                /\btotal nasal polyp score\b/i,
            ],
        },
        {
            id: 'nasal_congestion',
            label: 'Nasal congestion',
            category: 'clinicalEfficacy',
            patterns: [
                /\bnasal congestion\b/i,
                /\bnasal obstruction\b/i,
                /\bnasal blockage\b/i,
                /\bcongestion score\b/i,
                /\bnasal congestion score\b/i,
                /\bnasal obstruction score\b/i,
            ],
        },
        {
            id: 'snot_22',
            label: 'SNOT-22 / Sinonasal Outcome Test',
            category: 'humanistic',
            patterns: [
                /\bSNOT[- ]?22\b/i,
                /\bSinonasal Outcome Test[- ]?22\b/i,
                /\bSino[- ]nasal Outcome Test[- ]?22\b/i,
                /\b22[- ]item Sinonasal Outcome Test\b/i,
            ],
        },
        {
            id: 'sinus_opacification',
            label: 'Sinus opacification',
            category: 'clinicalEfficacy',
            patterns: [
                /\bsinus opacification\b/i,
                /\bsinus opacification score\b/i,
                /\bLund[- ]Mackay\b/i,
                /\bLund Mackay score\b/i,
                /\bCT opacification\b/i,
                /\bcomputed tomography opacification\b/i,
            ],
        },
    ],
    prurigo_nodularis: [
        {
            id: 'wi_nrs',
            label: 'WI-NRS',
            category: 'clinicalEfficacy',
            patterns: [
                /\bWI[- ]?NRS\b/i,
                /\bWI[- ]?NRS4\b/i,
                /\bWorst Itch N(?:umeric|umerical) Rating Scale\b/i,
                /\bWorst Itch NRS\b/i,
                /\bPP[- ]?NRS\b/i,
                /\bPeak Pruritus NRS\b/i,
                /\bPeak Pruritus N(?:umeric|umerical) Rating Scale\b/i,
            ],
        },
        {
            id: 'wi_nrs_ge4',
            label: 'WI-NRS ≥4-point improvement',
            category: 'clinicalEfficacy',
            patterns: [
                /\bWI[- ]?NRS.*(?:>=|≥)\s*4\b/i,
                /\b(?:>=|≥)\s*4[- ]?point.*WI[- ]?NRS\b/i,
                /\bWorst Itch NRS 4\b/i,
            ],
        },
        {
            id: 'itch_nrs',
            label: 'Itch NRS',
            category: 'clinicalEfficacy',
            patterns: [/\bItch[- ]?NRS\b/i, /\bItch N(?:umeric|umerical) Rating Scale\b/i],
        },
        {
            id: 'ap_nrs',
            label: 'AP-NRS / Average Pruritus NRS',
            category: 'clinicalEfficacy',
            patterns: [
                /\bAP[- ]?NRS\b/i,
                /\bAverage Pruritus NRS\b/i,
                /\bAverage Pruritus N(?:umeric|umerical) Rating Scale\b/i,
            ],
        },
        {
            id: 'iga_cpg',
            label: 'IGA-CPG',
            category: 'clinicalEfficacy',
            patterns: [
                /\bIGA[- ]?CPG\b/i,
                /\bIGA[- ]?CPG[- ]?[AS]\b/i,
                /\bInvestigator'?s? Global Assessment[- ]Chronic Prurigo\b/i,
                /\bInvestigator'?s? Global Assessment for Chronic Prurigo\b/i,
            ],
        },
        {
            id: 'iga_cnpg',
            label: 'IGA-CNPG',
            category: 'clinicalEfficacy',
            patterns: [
                /\bIGA[- ]?CNPG\b/i,
                /\bIGA[- ]?CNPG[- ]?S\b/i,
                /\bInvestigator Global Assessment for stage of chronic nodular prurigo\b/i,
            ],
        },
        {
            id: 'prurigo_activity_score',
            label: 'Prurigo Activity Score',
            category: 'clinicalEfficacy',
            patterns: [
                /\bPrurigo Activity Score\b/i,
                /\bPAS item\b/i,
                /\bPAS[- ]?75\b/i,
                /\b(?:>=|≥)\s*75% healed lesions\b/i,
            ],
        },
        {
            id: 'nodule_count',
            label: 'Nodule count',
            category: 'clinicalEfficacy',
            patterns: [/\bnodule count\b/i, /\bnodules count\b/i, /\bnumber of nodules\b/i, /\bprurigo nodules\b/i],
        },
        {
            id: 'skin_lesions',
            label: 'Skin lesions',
            category: 'clinicalEfficacy',
            patterns: [
                /\bskin lesions\b/i,
                /\bprurigo lesions\b/i,
                /\bhealed prurigo lesions\b/i,
                /\bexcoriations?\/crusts\b/i,
            ],
        },
        {
            id: 'sleep_disturbance_nrs',
            label: 'Sleep Disturbance NRS',
            category: 'clinicalEfficacy',
            measureLabel: 'Sleep Disturbance NRS',
            patterns: [
                /\bSD[- ]?NRS\b(?!\s*(?:change|improvement|reduction)?\s*(?:of\s*)?(?:≥|>=|at least)\s*4\b)/i,
                /\bSleep Disturbance(?:\s*\(SD\))?[- ]?NRS\b/i,
                /\bSleep Disturbance Numeric(?:al)? Rating Scale\b/i,
            ],
        },
        {
            id: 'sleep_disturbance_nrs_ge4_point_improvement',
            label: 'SD-NRS ≥4-Point Improvement',
            category: 'clinicalEfficacy',
            measureLabel: 'Sleep Disturbance NRS',
            thresholdLabel: 'SD-NRS ≥4-Point Improvement',
            patterns: [
                /\bSD[- ]?NRS\b.{0,40}\b(?:≥|>=|at least)\s*4[- ]?points?\b/i,
                /\bSD[- ]?NRS\b.{0,40}\b(?:improvement|decrease|reduction)\s+(?:of\s*)?(?:≥|>=|at least)\s*4[- ]?points?\b/i,
                /\b(?:≥|>=|at least)\s*4[- ]?points?\s+(?:improvement|decrease|reduction)\s+in\s+(?:the\s+)?SD[- ]?NRS\b/i,
                /\b(?:improvement|decrease|reduction)\s+(?:of\s*)?(?:≥|>=|at least)\s*4[- ]?points?\s+(?:from baseline\s+)?in\s+(?:the\s+)?SD[- ]?NRS\b/i,
                /\b(?:improvement|decrease|reduction)\s+(?:of\s*)?(?:≥|>=|at least)\s*4[- ]?points?\s+from baseline\s+in\s+(?:the\s+)?Sleep Disturbance(?:\s*\(SD\))?[- ]?NRS\b/i,
                /\bSleep Disturbance(?:\s*\(SD\))?[- ]?NRS\b.{0,40}\b(?:≥|>=|at least)\s*4[- ]?points?\b/i,
                /\bSleep Disturbance Numeric(?:al)? Rating Scale\b.{0,40}\b(?:≥|>=|at least)\s*4[- ]?points?\b/i,
            ],
        },
        {
            id: 'sleep_quality_nrs',
            label: 'Sleep Quality NRS',
            category: 'humanistic',
            patterns: [
                /\bSQ[- ]?NRS\b/i,
                /\bSleep Quality NRS\b/i,
                /\bSleep Quality N(?:umeric|umerical) Rating Scale\b/i,
            ],
        },
        {
            id: 'wp_nrs',
            label: 'Worst Pain NRS',
            category: 'humanistic',
            measureLabel: 'Worst Pain NRS',
            patterns: [
                /\bWP[- ]?NRS\b(?!\s*(?:improvement|response|responder|decrease|reduction|≥|>=|at least)\b)/i,
                /\bWorst Pain[- ]?NRS\b/i,
                /\bWorst Pain N(?:umeric|umerical) Rating Scale(?:\s*\(\s*WP[- ]?NRS\s*\))?\b/i,
            ],
        },
        {
            id: 'wp_nrs_ge4_point_improvement',
            label: 'WP-NRS ≥4-Point Improvement',
            category: 'humanistic',
            measureLabel: 'Worst Pain NRS',
            thresholdLabel: 'WP-NRS ≥4-Point Improvement',
            patterns: [
                /\bimprovement\s+in\s+(?:the\s+)?WP[- ]?NRS\s+by\s+(?:≥|>=|at least)\s*4(?:\s+points?)?(?:\s+from baseline)?\b/i,
                /\b(?:≥|>=|at least)\s*(?:a\s+)?4[- ]?point\s+(?:improvement|decrease|reduction)\s+in\s+(?:the\s+)?WP[- ]?NRS\b/i,
                /\bWP[- ]?NRS\b.{0,45}\b(?:improvement|decrease|reduction)\b.{0,25}\b(?:≥|>=|at least)\s*4(?:\s+points?)?\b/i,
                /\bWP[- ]?NRS\b.{0,45}\b(?:≥|>=|at least)\s*4[- ]?points?\s+(?:improvement|decrease|reduction)\b/i,
                /\bWP[- ]?NRS\s+4[- ]?point\s+(?:response|responder)\b/i,
                /\bachiev(?:e|ed|es|ing)\s+(?:an?\s+)?(?:≥|>=|at least)\s*4[- ]?point\s+(?:improvement|decrease|reduction)\s+in\s+(?:the\s+)?WP[- ]?NRS\b/i,
                /\bWorst Pain[- ]?NRS\b.{0,45}\b(?:≥|>=|at least)\s*4(?:\s+points?)?\b/i,
                /\bWorst Pain N(?:umeric|umerical) Rating Scale\b.{0,45}\b(?:≥|>=|at least)\s*4(?:\s+points?)?\b/i,
            ],
        },
        {
            id: 'dlqi',
            label: 'DLQI',
            category: 'humanistic',
            measureLabel: 'DLQI',
            patterns: [
                /\bDLQI\b(?!\s*(?:score\s*)?(?:[-–—]?\s*0\s*\/\s*1|[-–—]?\s*0\s+(?:or|and)\s+1|(?:≥|>=|at least)\s*4))/i,
                /\bDermatology Life Quality Index\b/i,
            ],
        },
        {
            id: 'dlqi_ge4_point_improvement',
            label: 'DLQI ≥4-Point Improvement',
            category: 'humanistic',
            measureLabel: 'DLQI',
            thresholdLabel: 'DLQI ≥4-Point Improvement',
            patterns: [
                /\bDLQI(?:\s+(?:total\s+)?score)?\b.{0,40}\b(?:≥|>=|at least)\s*4[- ]?points?\b/i,
                /\bDLQI(?:\s+(?:total\s+)?score)?\b.{0,40}\b(?:decreas(?:e|ed)|reduc(?:e|ed|tion)|improv(?:e|ed|ement))\b.{0,20}\b(?:≥|>=|at least)\s*4[- ]?points?\b/i,
                /\b(?:≥|>=|at least)\s*4[- ]?points?\s+(?:decrease|reduction|improvement)\s+in\s+(?:the\s+)?DLQI(?:\s+(?:total\s+)?score)?\b/i,
                /\b(?:decrease|reduction|improvement)\s+of\s+(?:≥|>=|at least)\s*4[- ]?points?\s+in\s+(?:the\s+)?DLQI(?:\s+(?:total\s+)?score)?\b/i,
                /\bDLQI(?:\s+(?:total\s+)?score)?\s+(?:decrease|reduction|improvement)\s+(?:of\s+)?(?:≥|>=|at least)\s*4[- ]?points?\b/i,
                /\bclinically meaningful improvement in\s+(?:the\s+)?DLQI\b/i,
                /\bDLQI\s+(?:minimal clinically important difference|MCID)\b/i,
            ],
        },
        {
            id: 'dlqi_0_1',
            label: 'DLQI 0/1',
            category: 'humanistic',
            measureLabel: 'DLQI',
            thresholdLabel: 'DLQI 0/1',
            patterns: [
                /\bDLQI(?:\s+(?:total\s+)?score)?\s*(?:of\s*)?[-–—]?\s*0\s*\/\s*1\b/i,
                /\bDLQI(?:\s+(?:total\s+)?score)?\s*(?:of\s*)?[-–—]?\s*0\s+(?:or|and)\s+1\b/i,
                /\bDLQI(?:\s+(?:total\s+)?score)?\s*(?:of\s*)?[-–—]?\s*(?:0|1)\b/i,
                /\bachiev(?:e|ed|es|ing)\s+(?:a\s+)?DLQI(?:\s+(?:total\s+)?score)?\s*(?:of\s*)?0\s*\/\s*1\b/i,
                /\bachiev(?:e|ed|es|ing)\s+(?:a\s+)?DLQI(?:\s+(?:total\s+)?score)?\s*(?:of\s*)?0\s+(?:or|and)\s+1\b/i,
                /\bDLQI(?:\s+(?:total\s+)?score)?\s+indicat(?:ing|ive of)\s+(?:no|minimal)\s+(?:effect|impact)\b/i,
            ],
        },
    ],
    copd: [
        {
            id: 'fev1',
            label: 'FEV1 / forced expiratory volume',
            category: 'clinicalEfficacy',
            patterns: [/\bFEV[- ]?1\b/i, /\bforced expiratory volume(?: in 1 second| in one second)?\b/i],
        },
        {
            id: 'spirometry',
            label: 'Spirometry',
            category: 'clinicalEfficacy',
            patterns: [/\bspirometry\b/i, /\brespiratory spirometry\b/i],
        },
        {
            id: 'copd_exacerbations',
            label: 'COPD exacerbations',
            category: 'clinicalEfficacy',
            patterns: [
                /\bCOPD exacerbations?\b/i,
                /\bacute exacerbations? of COPD\b/i,
                /\bAECOPD\b/i,
                /\bmoderate exacerbations?\b/i,
                /\bsevere exacerbations?\b/i,
                /\bannuali[sz]ed exacerbation rate\b/i,
                /\bexacerbation rate\b/i,
            ],
        },
        {
            id: 'sgrq',
            label: 'SGRQ / St George’s Respiratory Questionnaire',
            category: 'humanistic',
            patterns: [
                /\bSGRQ\b/i,
                /\bSGRQ[- ]?C\b/i,
                /\bSt\.?\s*George'?s Respiratory Questionnaire\b/i,
                /\bSaint George'?s Respiratory Questionnaire\b/i,
                /\bst george\b/i,
            ],
        },
        {
            id: 'six_minute_walk_test',
            label: '6-MWT / 6-Minute Walk Test',
            category: 'clinicalEfficacy',
            patterns: [
                /\b6[- ]?MWT\b/i,
                /\b6MWT\b/i,
                /\b6[- ]minute walk test\b/i,
                /\bSix[- ]Minute Walk Test\b/i,
                /\bSMWT\b/i,
            ],
        },
    ],
    weight_loss: [
        {
            id: 'percent_body_weight_change',
            label: 'Percent change in body weight',
            category: 'clinicalEfficacy',
            patterns: [
                /\bpercent(?:age)? change in body weight\b/i,
                /\b% change in body weight\b/i,
                /\bpercent(?:age)? body weight change\b/i,
                /\bbody weight reduction\b/i,
                /\bchange from baseline in body weight\b/i,
                /\bmean change in body weight\b/i,
            ],
        },
        {
            id: 'absolute_weight_change',
            label: 'Absolute weight change',
            category: 'clinicalEfficacy',
            patterns: [
                /\babsolute weight change\b/i,
                /\bchange in body weight \(?kg\)?\b/i,
                /\bweight loss in kg\b/i,
                /\bkilograms? lost\b/i,
                /\bbody weight \(?kg\)?\b/i,
            ],
        },
        {
            id: 'weight_loss_5_percent',
            label: '≥5% weight loss',
            category: 'clinicalEfficacy',
            patterns: [
                /\b(?:>=|≥)\s*5% (?:body )?weight loss\b/i,
                /\b(?:>=|≥)\s*5% (?:body )?weight reduction\b/i,
                /\b(?:at least )?5% (?:body )?weight loss\b/i,
                /\bachiev(?:e|ing) (?:>=|≥)?\s*5% (?:body )?weight loss\b/i,
            ],
        },
        {
            id: 'weight_loss_10_percent',
            label: '≥10% weight loss',
            category: 'clinicalEfficacy',
            patterns: [
                /\b(?:>=|≥)\s*10% (?:body )?weight loss\b/i,
                /\b(?:>=|≥)\s*10% (?:body )?weight reduction\b/i,
                /\b(?:at least )?10% (?:body )?weight loss\b/i,
                /\bachiev(?:e|ing) (?:>=|≥)?\s*10% (?:body )?weight loss\b/i,
            ],
        },
        {
            id: 'weight_loss_15_percent',
            label: '≥15% weight loss',
            category: 'clinicalEfficacy',
            patterns: [
                /\b(?:>=|≥)\s*15% (?:body )?weight loss\b/i,
                /\b(?:>=|≥)\s*15% (?:body )?weight reduction\b/i,
                /\b(?:at least )?15% (?:body )?weight loss\b/i,
                /\bachiev(?:e|ing) (?:>=|≥)?\s*15% (?:body )?weight loss\b/i,
            ],
        },
        {
            id: 'weight_loss_20_percent',
            label: '≥20% weight loss',
            category: 'clinicalEfficacy',
            patterns: [
                /\b(?:>=|≥)\s*20% (?:body )?weight loss\b/i,
                /\b(?:>=|≥)\s*20% (?:body )?weight reduction\b/i,
                /\b(?:at least )?20% (?:body )?weight loss\b/i,
                /\bachiev(?:e|ing) (?:>=|≥)?\s*20% (?:body )?weight loss\b/i,
            ],
        },
        {
            id: 'weight_loss_25_percent',
            label: '≥25% weight loss',
            category: 'clinicalEfficacy',
            patterns: [
                /\b(?:>=|≥)\s*25% (?:body )?weight loss\b/i,
                /\b(?:>=|≥)\s*25% (?:body )?weight reduction\b/i,
                /\b(?:at least )?25% (?:body )?weight loss\b/i,
                /\bachiev(?:e|ing) (?:>=|≥)?\s*25% (?:body )?weight loss\b/i,
            ],
        },
        {
            id: 'bmi',
            label: 'BMI / body mass index',
            category: 'clinicalEfficacy',
            patterns: [/\bBMI\b/i, /\bbody mass index\b/i, /\bchange in BMI\b/i, /\bpercent(?:age)? change in BMI\b/i],
        },
        {
            id: 'waist_circumference',
            label: 'Waist circumference',
            category: 'clinicalEfficacy',
            patterns: [
                /\bwaist circumference\b/i,
                /\bchange in waist circumference\b/i,
                /\bwaist circumference reduction\b/i,
            ],
        },
        {
            id: 'body_composition',
            label: 'Body composition',
            category: 'clinicalEfficacy',
            patterns: [
                /\bbody composition\b/i,
                /\bfat mass\b/i,
                /\blean mass\b/i,
                /\blean body mass\b/i,
                /\bvisceral adipose tissue\b/i,
                /\bvisceral fat\b/i,
                /\bDEXA\b/i,
                /\bDXA\b/i,
            ],
        },
        {
            id: 'weight_maintenance',
            label: 'Weight maintenance',
            category: 'clinicalEfficacy',
            patterns: [
                /\bweight maintenance\b/i,
                /\bmaintenance of weight loss\b/i,
                /\bmaintain(?:ed)? weight loss\b/i,
                /\bweight loss maintenance\b/i,
            ],
        },
        {
            id: 'weight_regain',
            label: 'Weight regain',
            category: 'clinicalEfficacy',
            patterns: [/\bweight regain\b/i, /\bregain(?:ed)? weight\b/i, /\btime to weight regain\b/i],
        },
        {
            id: 'glycemic_control',
            label: 'Glycemic control',
            category: 'clinicalEfficacy',
            patterns: [
                /\bglycemic control\b/i,
                /\bglycaemic control\b/i,
                /\bHbA1c\b/i,
                /\bA1c\b/i,
                /\bfasting glucose\b/i,
                /\bfasting plasma glucose\b/i,
                /\binsulin resistance\b/i,
                /\bHOMA[- ]?IR\b/i,
            ],
        },
        {
            id: 'blood_pressure',
            label: 'Blood pressure',
            category: 'clinicalEfficacy',
            patterns: [
                /\bblood pressure\b/i,
                /\bsystolic blood pressure\b/i,
                /\bdiastolic blood pressure\b/i,
                /\bSBP\b/i,
                /\bDBP\b/i,
            ],
        },
        {
            id: 'lipids',
            label: 'Lipids',
            category: 'clinicalEfficacy',
            patterns: [
                /\blipid profile\b/i,
                /\blipids\b/i,
                /\bLDL[- ]?C\b/i,
                /\bHDL[- ]?C\b/i,
                /\btriglycerides\b/i,
                /\bcholesterol\b/i,
            ],
        },
        {
            id: 'cardiometabolic_risk',
            label: 'Cardiometabolic risk',
            category: 'clinicalEfficacy',
            patterns: [
                /\bcardiometabolic risk\b/i,
                /\bcardiometabolic risk factors\b/i,
                /\bmetabolic syndrome\b/i,
                /\bcardiovascular risk factors\b/i,
            ],
        },
        {
            id: 'physical_function',
            label: 'Physical function',
            category: 'humanistic',
            patterns: [
                /\bphysical function\b/i,
                /\bphysical functioning\b/i,
                /\bmobility\b/i,
                /\bwalking capacity\b/i,
                /\bPROMIS physical function\b/i,
            ],
        },
        {
            id: 'iwqol_lite',
            label: 'IWQOL-Lite / weight-related quality of life',
            category: 'humanistic',
            patterns: [
                /\bIWQOL[- ]?Lite\b/i,
                /\bImpact of Weight on Quality of Life[- ]Lite\b/i,
                /\bweight[- ]related quality of life\b/i,
            ],
        },
        {
            id: 'appetite_hunger_cravings',
            label: 'Appetite / hunger / cravings',
            category: 'humanistic',
            patterns: [
                /\bappetite\b/i,
                /\bhunger\b/i,
                /\bfood cravings?\b/i,
                /\bsatiety\b/i,
                /\bfullness\b/i,
                /\bappetite control\b/i,
            ],
        },
        {
            id: 'eating_behavior',
            label: 'Eating behavior',
            category: 'humanistic',
            patterns: [
                /\beating behavior\b/i,
                /\beating behaviour\b/i,
                /\bfood intake\b/i,
                /\bcaloric intake\b/i,
                /\benergy intake\b/i,
                /\bbinge eating\b/i,
                /\bemotional eating\b/i,
                /\buncontrolled eating\b/i,
            ],
        },
    ],
}
